/**
 * Korean translation of ./en.ts (Travo's privacy policy and terms of use).
 * Provided for reference only: the English text governs.
 */
import { LEGAL_NAME, SUPPORT_EMAIL } from '@/shared/site';
import type { LegalCopy } from './types';

const EMAIL = SUPPORT_EMAIL;
const CLOUDFLARE_PRIVACY = 'https://www.cloudflare.com/privacypolicy/';
const GOOGLE_PRIVACY = 'https://policies.google.com/privacy?hl=ko';
const CONTACT = '[본 방침에 관해 어떻게 문의할 수 있습니까?](#contact)';

export const legalKo: LegalCopy = {
  eyebrow: '법적 고지',
  onThisPage: '이 페이지의 내용',
  inShort: '요약:',
  translationNote:
    '이 한국어 번역본은 참고용으로 제공됩니다. 영어 원문이 우선하며, 번역본과 영어 원문의 내용이 서로 다를 경우 영어 원문이 우선합니다.',
  readInEnglish: '영어 원문 읽기',

  privacy: {
    title: '개인정보 처리방침',
    updated: '최종 업데이트: 2026년 10월 25일',
    seo: {
      title: '개인정보 처리방침 | Travo',
      description:
        'Travo가 자동 여행 기록을 위한 위치 데이터를 포함하여 귀하의 정보를 어떻게 수집, 이용, 공유 및 보호하는지, 그리고 귀하에게 어떤 개인정보 보호 권리가 있는지 안내합니다.',
      keywords: 'Travo 개인정보 처리방침, 위치 데이터, 여행 기록 개인정보, 데이터 삭제, GDPR, CCPA'
    },
    sections: [
      {
        id: 'intro',
        heading: '본 방침에 대하여',
        blocks: [
          `Travo Labs라는 이름으로 운영되는 ${LEGAL_NAME} (이하 “**당사**”)의 본 개인정보 처리방침은 귀하가 당사의 서비스(“**서비스**”)를 이용할 때, 예를 들어 다음과 같은 경우에 당사가 귀하의 정보를 어떻게, 그리고 왜 수집, 저장, 이용 및/또는 공유(“**처리**”)할 수 있는지 설명합니다.`,
          [
            '당사의 모바일 애플리케이션(Travo) 또는 본 개인정보 처리방침에 링크된 당사의 기타 애플리케이션을 다운로드하여 이용하는 경우',
            '판매, 마케팅 또는 이벤트를 포함하여 기타 관련된 방식으로 당사와 교류하는 경우'
          ],
          `**질문이나 우려 사항이 있으신가요?** 본 개인정보 처리방침을 읽으시면 귀하의 개인정보 보호 권리와 선택 사항을 이해하는 데 도움이 됩니다. 당사의 정책 및 관행에 동의하지 않으시는 경우 당사의 서비스를 이용하지 마십시오. 그 밖에 질문이나 우려 사항이 있으시면 ${EMAIL}로 문의해 주십시오.`
        ]
      },
      {
        id: 'summary',
        heading: '주요 내용 요약',
        blocks: [
          '본 요약은 개인정보 처리방침의 주요 내용을 제공합니다. 각 항목 뒤의 링크를 따라가거나 목차에서 원하는 섹션을 찾아 해당 주제에 관한 자세한 내용을 확인할 수 있습니다.',
          '**당사는 어떤 개인정보를 처리합니까?** 귀하가 당사의 서비스를 방문, 이용 또는 탐색할 때, 당사는 귀하가 당사 및 서비스와 상호작용하는 방식, 귀하의 선택, 귀하가 이용하는 제품 및 기능에 따라 개인정보를 처리할 수 있습니다. [귀하가 당사에 제공하는 개인정보](#collect)에 대해 자세히 알아보십시오.',
          '**당사는 민감한 개인정보를 처리합니까?** 당사는 핵심 여행 기록 기능을 제공하기 위해 위치 데이터를 처리합니다. [당사가 귀하의 정보를 처리하는 방법](#process)에 대해 자세히 알아보십시오.',
          '**당사는 제3자로부터 정보를 받습니까?** 당사는 인증을 위한 소셜 미디어 플랫폼 등 귀하가 연결하기로 선택한 제3자 서비스로부터 정보를 받을 수 있습니다. [기타 출처에서 수집되는 정보](#collect)에 대해 자세히 알아보십시오.',
          '**당사는 귀하의 정보를 어떻게 처리합니까?** 당사는 서비스를 제공, 개선 및 관리하고, 귀하의 여행 이력을 자동으로 기록하며, 귀하의 여행 패턴을 분석하고, AI 기반 추천을 생성하며, 귀하와 소통하기 위해 귀하의 정보를 처리합니다. 당사는 유효한 법적 사유가 있는 경우에만 귀하의 정보를 처리합니다. [당사가 귀하의 정보를 처리하는 방법](#process)에 대해 자세히 알아보십시오.',
          '**당사는 어떤 상황에서 누구와 개인정보를 공유합니까?** 당사는 위치 서비스 제공업체, 클라우드 스토리지 제공업체, AI 처리 서비스, 앱 내 광고를 위한 Google AdMob을 포함한 특정 제3자와 특정 상황에서 정보를 공유할 수 있습니다. [당사가 언제, 누구와 귀하의 개인정보를 공유하는지](#sharing)에 대해 자세히 알아보십시오.',
          '**당사는 귀하의 정보를 어떻게 안전하게 보호합니까?** 당사는 귀하의 개인정보를 보호하기 위한 조직적·기술적 프로세스와 절차를 갖추고 있습니다. 그러나 인터넷을 통한 전자적 전송이나 정보 저장 기술은 100% 안전하다고 보장할 수 없으므로, 당사는 해커, 사이버 범죄자 또는 기타 권한 없는 제3자가 당사의 보안을 무력화하여 귀하의 정보를 부적절하게 수집, 접근, 탈취 또는 변경하지 못할 것이라고 약속하거나 보장할 수 없습니다. [당사가 귀하의 정보를 안전하게 보호하는 방법](#security)에 대해 자세히 알아보십시오.',
          '**귀하에게는 어떤 권리가 있습니까?** 귀하가 지리적으로 위치한 곳에 따라, 적용되는 개인정보 보호법에 의해 귀하는 개인정보와 관련하여 특정 권리를 가질 수 있습니다. [귀하의 개인정보 보호 권리](#rights)에 대해 자세히 알아보십시오.',
          `**귀하의 권리는 어떻게 행사합니까?** 권리를 행사하는 가장 쉬운 방법은 ${EMAIL}로 당사에 연락하는 것입니다. 당사는 관련 데이터 보호법에 따라 모든 요청을 검토하고 그에 따라 조치합니다.`
        ]
      },
      {
        id: 'collect',
        heading: '1. 당사는 어떤 정보를 수집합니까?',
        blocks: [
          { h3: '귀하가 당사에 제공하는 개인정보' },
          { inShort: '당사는 귀하가 당사에 제공하는 개인정보를 수집합니다.' },
          '당사는 귀하가 서비스에 등록하거나, 당사 또는 당사의 제품 및 서비스에 관한 정보를 얻는 데 관심을 표명하거나, 서비스 내 활동에 참여하거나, 그 밖에 당사에 연락할 때 귀하가 자발적으로 제공하는 개인정보를 수집합니다.',
          '**귀하가 제공하는 개인정보.** 당사가 수집하는 개인정보는 귀하와 당사 및 서비스 간 상호작용의 맥락, 귀하의 선택, 귀하가 이용하는 제품 및 기능에 따라 달라집니다. 당사가 수집하는 개인정보에는 다음이 포함될 수 있습니다.',
          ['이름', '이메일 주소', '전화번호', '사용자 이름', '비밀번호', '프로필 사진', '연락처 또는 인증 데이터'],
          '**민감한 정보.** 당사는 서비스 제공에 필요한 경우 다음과 같은 민감한 정보를 처리합니다.',
          [
            '**위치 데이터:** 귀하가 백그라운드 위치 추적을 활성화하면, 당사는 귀하가 방문한 도시와 나라를 자동으로 기록하기 위해 정밀한 위치 정보를 지속적으로 수집합니다',
            '**사진:** 여행 추억을 만들기 위해 귀하가 업로드하기로 선택한 이미지'
          ],
          '**소셜 미디어 로그인 데이터.** 당사는 귀하에게 Google, Apple 또는 기타 소셜 미디어 계정 등 기존 소셜 미디어 계정 정보를 사용하여 당사에 등록할 수 있는 옵션을 제공할 수 있습니다. 귀하가 이 방식으로 등록하기로 선택하는 경우, 당사는 아래 “[당사는 귀하의 소셜 로그인을 어떻게 처리합니까?](#social-logins)” 섹션에 설명된 정보를 수집합니다.',
          '귀하가 당사에 제공하는 모든 개인정보는 사실이며 완전하고 정확해야 하며, 귀하는 해당 개인정보에 변경 사항이 있으면 당사에 알려야 합니다.',
          { h3: '자동으로 수집되는 정보' },
          { inShort: '귀하가 당사의 서비스를 방문할 때 인터넷 프로토콜(IP) 주소 및/또는 브라우저와 기기 특성 등 일부 정보가 자동으로 수집됩니다.' },
          '당사는 귀하가 서비스를 방문, 이용 또는 탐색할 때 특정 정보를 자동으로 수집합니다. 이 정보는 귀하의 구체적인 신원(이름이나 연락처 정보 등)을 드러내지는 않지만, IP 주소, 브라우저 및 기기 특성, 운영체제, 언어 설정, 참조 URL, 기기 이름, 국가, 위치, 귀하가 당사의 서비스를 언제 어떻게 이용하는지에 관한 정보 및 기타 기술 정보와 같은 기기 및 이용 정보를 포함할 수 있습니다. 이 정보는 주로 당사 서비스의 보안과 운영을 유지하고, 당사의 내부 분석 및 보고 목적을 위해 필요합니다.',
          '당사가 수집하는 정보는 다음과 같습니다.',
          [
            '**위치 데이터.** 당사는 정밀한 위치 정보와 대략적인 위치 정보를 모두 수집합니다. 정밀한 위치 데이터는 GPS 및 기타 기술을 사용하여 귀하의 정확한 현재 위치를 알려 주는 위치 정보를 수집합니다. 대략적인 위치 데이터는 귀하의 대략적인 위치를 제공합니다. 당사가 수집하는 정보의 양은 귀하가 서비스에 접속하는 데 사용하는 기기의 유형과 설정에 따라 달라집니다. 귀하는 해당 정보에 대한 접근을 거부하거나 기기의 위치 설정을 비활성화하여 당사의 이 정보 수집을 거부(옵트아웃)할 수 있습니다. 다만 위치 추적은 Travo 기능의 핵심이므로, 수집을 거부하기로 선택하는 경우 서비스의 일부 기능을 이용하지 못할 수 있습니다.',
            '**사용자 ID 및 기기 ID.** 당사는 제품 개인화, 앱 기능 및 분석을 위해 사용자 ID(귀하의 신원과 연결되며 추적 목적으로 사용됨) 및 기기 ID를 포함한 고유 식별자를 수집합니다.',
            '**이용 데이터.** 당사는 귀하가 당사의 서비스를 어떻게 이용하는지 이해하기 위해 귀하의 제품 상호작용 및 기타 이용 데이터를 수집하며, 이를 통해 분석과 제품 개인화를 제공하고 앱 기능을 개선합니다.',
            '**진단 데이터.** 당사는 서비스의 안정성과 성능을 분석하고 개선하기 위해 충돌 데이터, 성능 데이터 및 기타 진단 정보를 수집합니다. 이 데이터는 오로지 분석 목적으로만 사용됩니다.',
            '**로그 및 이용 데이터.** 로그 및 이용 데이터는 귀하가 당사의 서비스에 접속하거나 이를 이용할 때 당사 서버가 자동으로 수집하여 로그 파일에 기록하는 서비스 관련 정보, 진단 정보, 이용 정보 및 성능 정보입니다. 귀하가 당사와 상호작용하는 방식에 따라, 이 로그 데이터에는 IP 주소, 기기 정보, 브라우저 유형 및 설정, 서비스 내 귀하의 활동에 관한 정보(이용과 관련된 날짜/시간 기록, 조회한 페이지와 파일, 검색, 사용하는 기능 등 귀하가 수행하는 기타 행위), 기기 이벤트 정보(시스템 활동, 오류 보고서(“크래시 덤프”라고도 함), 하드웨어 설정 등)가 포함될 수 있습니다.',
            '**기기 데이터.** 당사는 귀하가 서비스에 접속하는 데 사용하는 휴대전화, 태블릿 또는 기타 기기에 관한 정보와 같은 기기 데이터를 수집합니다. 사용하는 기기에 따라, 이 기기 데이터에는 IP 주소(또는 프록시 서버), 기기 및 애플리케이션 식별 번호, 위치, 브라우저 유형, 하드웨어 모델, 인터넷 서비스 제공업체 및/또는 이동통신사, 운영체제, 시스템 구성 정보 등의 정보가 포함될 수 있습니다.'
          ],
          { h3: '기타 출처에서 수집되는 정보' },
          { inShort: '당사는 귀하가 당사 앱에 연결하는 제3자 서비스로부터 제한된 데이터를 수집할 수 있습니다.' },
          '당사는 다음과 같은 기타 출처로부터 귀하에 관한 정보를 얻을 수 있습니다.',
          [
            '**지도 및 위치 서비스**(예: Mapbox, OpenStreetMap): GPS 좌표를 도시 및 국가 이름으로 변환하기 위해',
            '**소셜 미디어 플랫폼**: 귀하가 소셜 로그인을 사용하여 인증하기로 선택하는 경우',
            '**클라우드 스토리지 서비스**: 귀하의 여행 데이터와 사진이 안전하게 저장되는 곳'
          ]
        ]
      },
      {
        id: 'process',
        heading: '2. 당사는 귀하의 정보를 어떻게 처리합니까?',
        blocks: [
          { inShort: '당사는 서비스를 제공, 개선 및 관리하고, 귀하와 소통하며, 보안 및 사기 방지를 위해, 그리고 법률을 준수하기 위해 귀하의 정보를 처리합니다. 또한 귀하의 동의를 받아 기타 목적으로 귀하의 정보를 처리할 수 있습니다.' },
          '**당사는 귀하가 당사의 서비스와 상호작용하는 방식에 따라 다음을 포함한 다양한 이유로 귀하의 개인정보를 처리합니다.**',
          [
            '**계정 생성 및 인증을 지원하고 기타 사용자 계정을 관리하기 위해.** 당사는 귀하가 계정을 생성하고 로그인할 수 있도록, 그리고 귀하의 계정이 정상적으로 작동하도록 유지하기 위해 귀하의 정보를 처리할 수 있습니다.',
            '**귀하의 여행 이력을 자동으로 기록하기 위해.** 당사는 귀하가 방문한 도시와 나라를 자동으로 기록하고, 각 장소에서 보낸 일수를 계산하며, 귀하의 여정에 관한 종합적인 타임라인을 만들기 위해 백그라운드에서 귀하의 위치 데이터를 처리합니다.',
            '**귀하의 여행 패턴을 분석하기 위해.** 당사는 방문한 총 국가 수, 둘러본 도시, 여행 빈도 및 귀하의 여행 행태에 관한 기타 인사이트 등의 통계를 제공하기 위해 귀하의 여행 이력 데이터를 처리합니다.',
            '**AI 기반 추천을 생성하기 위해.** 당사는 귀하의 여행 스타일과 선호도에 기반한 개인 맞춤형 여행지 추천을 제공하기 위해 귀하의 여행 이력과 패턴을 처리할 수 있습니다.',
            '**여행 추억을 만들고 관리하기 위해.** 당사는 귀하가 체계적이고 시각적인 여행 기록을 만들 수 있도록 귀하가 업로드한 사진과 관련 위치 데이터를 처리합니다.',
            '**지도 시각화를 제공하기 위해.** 당사는 인터랙티브 지도에 귀하가 방문한 장소를 표시하고 국경을 보여 주기 위해 귀하의 위치 데이터를 처리합니다.',
            '**귀하에게 관리상 정보를 보내기 위해.** 당사는 당사의 제품 및 서비스에 관한 세부 정보, 당사 약관 및 정책의 변경 사항, 기타 이와 유사한 정보를 귀하에게 보내기 위해 귀하의 정보를 처리할 수 있습니다.',
            '**피드백을 요청하기 위해.** 당사는 필요한 경우 피드백을 요청하고 귀하의 서비스 이용에 관해 귀하에게 연락하기 위해 귀하의 정보를 처리할 수 있습니다.',
            '**맞춤형 광고 및 마케팅을 위해.** 당사는 귀하의 선호 및 관련 법률에 따라 개발자의 광고 또는 마케팅, 제품 개인화, 제3자 광고 목적으로 귀하의 정보(이름, 이메일 주소, 사용자 ID 포함)를 처리할 수 있습니다. 이 데이터는 귀하의 신원과 연결되며 추적 목적으로 사용될 수 있습니다.',
            '**마케팅 및 홍보 커뮤니케이션을 보내기 위해.** 귀하의 마케팅 수신 설정에 부합하는 경우, 당사는 귀하가 당사에 보낸 개인정보를 당사의 마케팅 목적으로 처리할 수 있습니다. 귀하는 언제든지 당사의 마케팅 이메일 수신을 거부할 수 있습니다. 자세한 내용은 아래 “[귀하의 개인정보 보호 권리는 무엇입니까?](#rights)”를 참조하십시오.',
            '**당사의 서비스를 보호하기 위해.** 당사는 사기 모니터링 및 방지를 포함하여 서비스를 안전하고 보안이 유지되도록 하기 위한 노력의 일환으로 귀하의 정보를 처리할 수 있습니다.',
            '**이용 동향을 파악하기 위해.** 당사는 서비스가 어떻게 이용되고 있는지 더 잘 이해하여 이를 개선할 수 있도록, 귀하가 당사의 서비스를 이용하는 방식에 관한 정보를 처리할 수 있습니다.',
            '**개인의 중대한 이익을 구하거나 보호하기 위해.** 당사는 위해를 방지하는 등 개인의 중대한 이익을 구하거나 보호하기 위해 필요한 경우 귀하의 정보를 처리할 수 있습니다.'
          ]
        ]
      },
      {
        id: 'legal-bases',
        heading: '3. 당사는 어떤 법적 근거에 따라 귀하의 정보를 처리합니까?',
        blocks: [
          { inShort: '당사는 필요하다고 판단되고, 관련 법률에 따른 유효한 법적 사유(즉, 법적 근거)가 있는 경우에만 귀하의 개인정보를 처리합니다. 예를 들어 귀하의 동의가 있는 경우, 법률을 준수하기 위한 경우, 계약을 체결하거나 계약상 의무를 이행하기 위해 귀하에게 서비스를 제공하는 경우, 귀하의 권리를 보호하기 위한 경우, 또는 당사의 정당한 사업상 이익을 달성하기 위한 경우입니다.' },
          { h3: '귀하가 EU 또는 영국에 있는 경우 이 섹션이 적용됩니다.' },
          '일반 개인정보 보호법(GDPR) 및 UK GDPR에 따라 당사는 귀하의 개인정보를 처리하기 위해 근거로 삼는 유효한 법적 근거를 설명해야 합니다. 이에 따라 당사는 다음과 같은 법적 근거에 따라 귀하의 개인정보를 처리할 수 있습니다.',
          [
            '**동의.** 귀하가 특정 목적을 위해 귀하의 개인정보를 이용하도록 당사에 허락(즉, 동의)한 경우 당사는 귀하의 정보를 처리할 수 있습니다. 귀하는 언제든지 동의를 철회할 수 있습니다. 아래에서 [동의 철회](#rights)에 대해 자세히 알아보십시오.',
            '**계약의 이행.** 당사는 서비스 제공을 포함하여 귀하에 대한 당사의 계약상 의무를 이행하기 위해 필요하다고 판단되는 경우, 또는 귀하와 계약을 체결하기 전에 귀하의 요청에 따라 귀하의 개인정보를 처리할 수 있습니다.',
            '**정당한 이익.** 당사는 당사의 정당한 사업상 이익을 달성하기 위해 합리적으로 필요하다고 판단되고, 그러한 이익이 귀하의 이익과 기본적 권리 및 자유보다 우월하지 않은 경우 귀하의 정보를 처리할 수 있습니다. 예를 들어 당사는 설명된 목적 중 일부를 위해 다음과 같은 목적으로 귀하의 개인정보를 처리할 수 있습니다: 사용자를 유치하고 유지할 수 있도록 서비스가 어떻게 이용되는지 분석하여 개선하는 것, 문제를 진단하거나 사기 행위를 방지하는 것, 그리고 사용자 경험을 개선할 수 있도록 사용자가 당사의 제품과 서비스를 어떻게 이용하는지 이해하는 것.',
            '**법적 의무.** 당사는 법 집행 기관이나 규제 기관에 협조하거나, 당사의 법적 권리를 행사 또는 방어하거나, 당사가 관련된 소송에서 귀하의 정보를 증거로 공개하는 등 당사의 법적 의무를 준수하기 위해 필요하다고 판단되는 경우 귀하의 정보를 처리할 수 있습니다.',
            '**중대한 이익.** 당사는 어떤 사람의 안전에 대한 잠재적 위협이 수반되는 상황 등 귀하 또는 제3자의 중대한 이익을 보호하기 위해 필요하다고 판단되는 경우 귀하의 정보를 처리할 수 있습니다.'
          ],
          { h3: '귀하가 캐나다에 있는 경우 이 섹션이 적용됩니다.' },
          '귀하가 특정 목적을 위해 귀하의 개인정보를 이용하도록 당사에 특정하여 허락(즉, 명시적 동의)한 경우, 또는 귀하의 허락을 추정할 수 있는 상황(즉, 묵시적 동의)에서 당사는 귀하의 정보를 처리할 수 있습니다. 귀하는 언제든지 [동의를 철회](#rights)할 수 있습니다.',
          '일부 예외적인 경우에는 관련 법률에 따라 귀하의 동의 없이 귀하의 정보를 처리하는 것이 법적으로 허용될 수 있으며, 그 예는 다음과 같습니다.',
          [
            '수집이 명백히 개인의 이익에 부합하고 적시에 동의를 얻을 수 없는 경우',
            '조사 및 사기 탐지와 방지를 위한 경우',
            '특정 조건이 충족되는 경우의 사업 거래를 위한 경우',
            '증인 진술서에 포함되어 있고, 보험금 청구를 평가, 처리 또는 해결하기 위해 수집이 필요한 경우',
            '부상자, 환자 또는 사망자의 신원을 확인하고 그 근친과 연락하기 위한 경우',
            '개인이 금융 학대의 피해자였거나, 피해자이거나, 피해자가 될 수 있다고 믿을 만한 합리적인 근거가 있는 경우',
            '동의를 받아 수집하고 이용하면 정보의 가용성이나 정확성이 훼손될 것으로 합리적으로 예상되고, 그 수집이 계약 위반 또는 캐나다나 주(州)의 법률 위반 조사와 관련된 목적상 합리적인 경우',
            '기록의 제출과 관련된 소환장, 영장, 법원 명령 또는 법원 규칙을 준수하기 위해 공개가 요구되는 경우',
            '개인이 고용, 사업 또는 직업 활동 과정에서 생성한 정보이고, 그 수집이 해당 정보가 생성된 목적에 부합하는 경우',
            '수집이 오로지 저널리즘, 예술 또는 문학 목적인 경우',
            '정보가 공개적으로 이용 가능하며 규정에 명시된 경우'
          ]
        ]
      },
      {
        id: 'sharing',
        heading: '4. 당사는 언제, 누구와 귀하의 개인정보를 공유합니까?',
        blocks: [
          { inShort: '당사는 이 섹션에 설명된 특정 상황에서 및/또는 다음 제3자와 정보를 공유할 수 있습니다.' },
          '**공급업체, 컨설턴트 및 기타 제3자 서비스 제공업체.** 당사는 당사를 위해 또는 당사를 대신하여 서비스를 수행하고 그 업무를 위해 해당 정보에 접근해야 하는 제3자 공급업체, 서비스 제공업체, 계약업체 또는 대리인(“**제3자**”)과 귀하의 데이터를 공유할 수 있습니다. 당사는 귀하의 개인정보를 보호하는 데 도움이 되도록 설계된 계약을 제3자와 체결하고 있습니다. 이는 당사가 지시하지 않는 한 제3자가 귀하의 개인정보로 어떠한 행위도 할 수 없음을 의미합니다. 또한 제3자는 당사 이외의 어떤 조직과도 귀하의 개인정보를 공유하지 않습니다. 아울러 제3자는 당사를 대신하여 보유하는 데이터를 보호하고 당사가 지시하는 기간 동안 보관할 것을 약속합니다. 당사가 개인정보를 공유할 수 있는 제3자는 다음과 같습니다.',
          [
            '**클라우드 컴퓨팅 및 스토리지 서비스:** Google Firebase 및 Google Cloud(인증, 데이터베이스, 푸시 알림 및 충돌 보고)',
            '**위치 및 지도 서비스:** Mapbox(도시/위치 검색 및 지도 시각화); OpenStreetMap Nominatim(역지오코딩); Google Maps API(지도 표시 및 국경)',
            '**AI 및 머신러닝 서비스:** Google Gemini; Anthropic Claude(추천 생성)',
            '**사용자 계정 등록 및 인증:** Google 로그인; Apple 로그인',
            '**분석 서비스:** Google Analytics for Firebase 및 Mixpanel(모바일 앱); Cloudflare Web Analytics(본 웹사이트)',
            '**광고 서비스:** Google AdMob(Travo 앱에서 광고를 표시하고 측정)'
          ],
          '당사는 다음과 같은 상황에서도 귀하의 개인정보를 공유해야 할 수 있습니다.',
          [
            '**사업 양도.** 당사는 합병, 회사 자산 매각, 자금 조달 또는 당사 사업의 전부나 일부를 다른 회사가 인수하는 것과 관련하여, 또는 이에 관한 협상 중에 귀하의 정보를 공유하거나 이전할 수 있습니다.',
            '**계열사.** 당사는 계열사와 귀하의 정보를 공유할 수 있으며, 이 경우 해당 계열사가 본 개인정보 처리방침을 준수하도록 요구합니다. 계열사에는 당사의 모회사와 모든 자회사, 합작 투자 파트너, 또는 당사가 지배하거나 당사와 공동 지배하에 있는 기타 회사가 포함됩니다.'
          ]
        ]
      },
      {
        id: 'cookies',
        heading: '5. 당사는 쿠키 및 기타 추적 기술을 사용합니까?',
        blocks: [
          { inShort: '당사는 자체적으로 쿠키를 설정하지 않습니다. 본 웹사이트가 이용하는 서비스는 귀하의 IP 주소를 수신할 수 있으며, 동영상 플레이어는 귀하가 동영상을 재생하는 경우 쿠키를 설정할 수 있습니다. Travo 앱은 Google AdMob 광고를 표시하며, 귀하가 동의한 범위 내에서 기기 및 광고 데이터를 사용합니다.' },
          'www.travolabs.com은 Cloudflare가 호스팅하며, Cloudflare는 사이트를 제공하고 보호하기 위해 IP 주소를 처리합니다. 방문 수는 쿠키를 설정하지 않는 Cloudflare Web Analytics로 집계됩니다. 글꼴은 Google Fonts에서 로드되므로, 페이지가 로드될 때 Google이 귀하의 IP 주소를 수신합니다.',
          '당사는 자체적으로 쿠키를 설정하지 않습니다. Travo 페이지의 데모 동영상을 재생하면 동영상이 YouTube에서 로드되며, YouTube는 Google의 정책에 따라 쿠키를 설정할 수 있습니다. 자세한 내용은 [Cloudflare 개인정보 처리방침](' + CLOUDFLARE_PRIVACY + ') 및 [Google 개인정보처리방침](' + GOOGLE_PRIVACY + ')에서 확인하십시오.',
          '모바일 애플리케이션은 모바일 전용 추적 기술을 사용할 수 있습니다. Travo 앱은 분석 및 충돌 보고를 위해 “[당사는 어떤 정보를 수집합니까?](#collect)”에 설명된 식별자와 이용 데이터를 사용합니다.',
          'Travo 앱은 Google AdMob 광고도 표시합니다. 광고를 표시 및 측정하고 사기를 방지하기 위해 Google은 귀하의 IP 주소, 기기 및 앱 정보, 광고와의 상호작용, 그리고 기기의 광고 식별자(iPhone에서는 귀하가 추적을 허용한 경우에만)를 수신할 수 있습니다.',
          '광고가 로드되기 전에 앱은 귀하가 거주하는 지역의 법률이 요구하는 동의를 받습니다. 유럽경제지역, 영국 및 스위스에서는 Google의 동의 양식을 먼저 표시하고, 이어서 iPhone에서는 Apple의 앱 추적 투명성(App Tracking Transparency) 요청을 표시합니다. 이후 앱 계정 화면의 ‘개인정보 보호 설정’(제공되는 경우), iOS의 설정 → 개인정보 보호 및 보안 → 추적, 또는 Android 기기의 광고 설정에서 선택을 변경할 수 있습니다. [Google 서비스를 사용하는 사이트 또는 앱의 정보를 Google이 사용하는 방식](https://policies.google.com/technologies/partner-sites?hl=ko)에 대해 자세히 알아보십시오.'
        ]
      },
      {
        id: 'social-logins',
        heading: '6. 당사는 귀하의 소셜 로그인을 어떻게 처리합니까?',
        blocks: [
          { inShort: '귀하가 소셜 미디어 계정을 사용하여 당사의 서비스에 등록하거나 로그인하기로 선택하는 경우, 당사는 귀하에 관한 특정 정보에 접근할 수 있습니다.' },
          '당사의 서비스는 귀하가 제3자 소셜 미디어 계정 정보(Google 또는 Apple 로그인 등)를 사용하여 등록하고 로그인할 수 있는 기능을 제공합니다. 귀하가 이를 선택하는 경우, 당사는 귀하의 소셜 미디어 제공업체로부터 귀하에 관한 특정 프로필 정보를 받게 됩니다. 당사가 받는 프로필 정보는 해당 소셜 미디어 제공업체에 따라 다를 수 있지만, 대개 귀하의 이름, 이메일 주소, 프로필 사진과 함께 귀하가 해당 소셜 미디어 플랫폼에 공개하기로 선택한 기타 정보가 포함됩니다.',
          '당사는 받은 정보를 본 개인정보 처리방침에 설명된 목적 또는 관련 서비스에서 귀하에게 달리 명확히 안내된 목적으로만 사용합니다. 당사는 귀하의 제3자 소셜 미디어 제공업체가 귀하의 개인정보를 그 밖의 방식으로 이용하는 것을 통제하지 않으며 이에 대해 책임지지 않는다는 점에 유의하십시오. 해당 제공업체가 귀하의 개인정보를 어떻게 수집, 이용 및 공유하는지, 그리고 해당 사이트와 앱에서 개인정보 설정을 어떻게 지정할 수 있는지 이해하기 위해 해당 제공업체의 개인정보 처리방침을 검토하실 것을 권장합니다.'
        ]
      },
      {
        id: 'retention',
        heading: '7. 당사는 귀하의 정보를 얼마 동안 보관합니까?',
        blocks: [
          { inShort: '당사는 법률에서 달리 요구하지 않는 한, 본 개인정보 처리방침에 명시된 목적을 달성하는 데 필요한 기간 동안 귀하의 정보를 보관합니다.' },
          '당사는 법률(세무, 회계 또는 기타 법적 요건 등)에 따라 더 긴 보관 기간이 요구되거나 허용되지 않는 한, 본 개인정보 처리방침에 명시된 목적에 필요한 기간 동안만 귀하의 개인정보를 보관합니다. 본 방침의 어떠한 목적도 사용자 계정 해지 후 구십(90)일을 초과하여 귀하의 개인정보를 보관할 것을 요구하지 않습니다.',
          '당사가 귀하의 개인정보를 처리할 지속적인 정당한 사업상 필요가 없는 경우, 당사는 해당 정보를 삭제하거나 익명화합니다. 이것이 불가능한 경우(예: 귀하의 개인정보가 백업 보관소에 저장되어 있는 경우)에는 삭제가 가능해질 때까지 귀하의 개인정보를 안전하게 저장하고 추가 처리로부터 격리합니다.',
          '**여행 이력 데이터:** 귀하의 위치 추적 데이터, 여행 통계 및 관련 추억은 귀하에게 지속적인 여행 기록 서비스를 제공하기 위해 귀하의 계정이 활성 상태인 동안 보관됩니다. 계정 삭제 시 이 데이터는 90일 이내에 영구적으로 삭제됩니다.'
        ]
      },
      {
        id: 'security',
        heading: '8. 당사는 귀하의 정보를 어떻게 안전하게 보호합니까?',
        blocks: [
          { inShort: '당사는 조직적·기술적 보안 조치 체계를 통해 귀하의 개인정보를 보호하고자 합니다.' },
          '당사는 당사가 처리하는 모든 개인정보의 보안을 보호하기 위해 설계된 적절하고 합리적인 기술적·조직적 보안 조치를 시행하고 있습니다. 그러나 당사의 보호 조치와 귀하의 정보를 보호하기 위한 노력에도 불구하고, 인터넷을 통한 전자적 전송이나 정보 저장 기술은 100% 안전하다고 보장할 수 없으므로, 당사는 해커, 사이버 범죄자 또는 기타 권한 없는 제3자가 당사의 보안을 무력화하여 귀하의 정보를 부적절하게 수집, 접근, 탈취 또는 변경하지 못할 것이라고 약속하거나 보장할 수 없습니다. 당사는 귀하의 개인정보를 보호하기 위해 최선을 다하겠지만, 당사 서비스와의 개인정보 송수신에 따른 위험은 귀하가 부담합니다. 귀하는 안전한 환경에서만 서비스에 접속해야 합니다.',
          '**당사가 시행하는 보안 조치는 다음과 같습니다.**',
          [
            '전송 중 및 저장 시 데이터 암호화',
            '안전한 인증 메커니즘',
            '정기적인 보안 평가',
            '업무상 알아야 할 필요가 있는 경우로 한정된 개인정보 접근',
            '엔터프라이즈급 제공업체를 통한 안전한 클라우드 스토리지'
          ]
        ]
      },
      {
        id: 'minors',
        heading: '9. 당사는 미성년자로부터 정보를 수집합니까?',
        blocks: [
          { inShort: '당사는 18세 미만 아동으로부터 고의로 데이터를 수집하거나 아동을 대상으로 마케팅하지 않습니다.' },
          `당사는 18세 미만 아동에게 고의로 데이터를 요청하거나 아동을 대상으로 마케팅하지 않습니다. 서비스를 이용함으로써 귀하는 귀하가 18세 이상이거나, 그러한 미성년자의 부모 또는 보호자로서 해당 미성년 피부양자의 서비스 이용에 동의함을 진술합니다. 18세 미만 사용자의 개인정보가 수집되었음을 알게 되는 경우, 당사는 해당 계정을 비활성화하고 당사 기록에서 해당 데이터를 신속히 삭제하기 위한 합리적인 조치를 취합니다. 당사가 18세 미만 아동으로부터 수집했을 수 있는 데이터를 알게 되시면 ${EMAIL}로 문의해 주십시오.`
        ]
      },
      {
        id: 'rights',
        heading: '10. 귀하의 개인정보 보호 권리는 무엇입니까?',
        blocks: [
          { inShort: '유럽경제지역(EEA), 영국(UK), 스위스, 캐나다 등 일부 지역에서 귀하는 개인정보에 대해 더 많은 접근과 통제를 허용하는 권리를 가집니다. 귀하는 언제든지 계정을 검토, 변경 또는 해지할 수 있습니다.' },
          `일부 지역(EEA, 영국, 스위스, 캐나다 등)에서 귀하는 관련 데이터 보호법에 따라 특정 권리를 가집니다. 여기에는 (i) 귀하의 개인정보에 대한 접근 및 사본 취득을 요청할 권리, (ii) 정정 또는 삭제를 요청할 권리, (iii) 귀하의 개인정보 처리를 제한할 권리, (iv) 해당되는 경우 데이터 이동에 관한 권리, (v) 자동화된 의사결정의 대상이 되지 않을 권리가 포함될 수 있습니다. 특정 상황에서는 귀하의 개인정보 처리에 반대할 권리도 가질 수 있습니다. 귀하는 아래 “${CONTACT}” 섹션에 제공된 연락처를 이용하여 당사에 연락함으로써 이러한 요청을 할 수 있습니다.`,
          '당사는 관련 데이터 보호법에 따라 모든 요청을 검토하고 그에 따라 조치합니다.',
          '귀하가 EEA 또는 영국에 있으며 당사가 귀하의 개인정보를 불법적으로 처리하고 있다고 생각하는 경우, 귀하는 [회원국 데이터 보호 기관](https://ec.europa.eu/justice/data-protection/bodies/authorities/index_en.htm) 또는 [영국 데이터 보호 기관](https://ico.org.uk/make-a-complaint/data-protection-complaints/data-protection-complaints/)에 불만을 제기할 권리도 있습니다.',
          '귀하가 스위스에 있는 경우 [연방 데이터 보호 및 정보 위원](https://www.edoeb.admin.ch/edoeb/en/home.html)에게 연락할 수 있습니다.',
          `**동의 철회:** 당사가 귀하의 개인정보를 처리하기 위해 귀하의 동의(관련 법률에 따라 명시적 및/또는 묵시적 동의일 수 있음)에 근거하는 경우, 귀하는 언제든지 동의를 철회할 권리가 있습니다. 귀하는 아래 “${CONTACT}” 섹션에 제공된 연락처를 이용하여 당사에 연락하거나, 기기 설정 또는 앱 내에서 위치 추적을 비활성화하여 언제든지 동의를 철회할 수 있습니다.`,
          '다만 이는 철회 전에 이루어진 처리의 적법성에 영향을 미치지 않으며, 관련 법률이 허용하는 경우 동의 이외의 적법한 처리 근거에 따라 수행되는 귀하의 개인정보 처리에도 영향을 미치지 않는다는 점에 유의하십시오.',
          `**마케팅 및 홍보 커뮤니케이션 수신 거부:** 귀하는 당사가 보내는 이메일의 수신 거부 링크를 클릭하거나, 아래 “${CONTACT}” 섹션에 제공된 연락처를 이용하여 당사에 연락함으로써 언제든지 당사의 마케팅 및 홍보 커뮤니케이션 수신을 거부할 수 있습니다. 그러면 귀하는 마케팅 목록에서 제외됩니다. 다만 당사는 계정 관리 및 이용에 필요한 서비스 관련 메시지를 보내거나, 서비스 요청에 응답하거나, 기타 마케팅 이외의 목적으로 여전히 귀하와 소통할 수 있습니다.`,
          { h3: '계정 정보' },
          '언제든지 계정의 정보를 검토 또는 변경하거나 계정을 해지하고자 하는 경우, 다음과 같이 할 수 있습니다.',
          ['Travo 모바일 애플리케이션 내 계정 설정에 접속', '제공된 연락처 정보를 이용하여 당사에 연락'],
          '귀하가 계정 해지를 요청하면, 당사는 활성 데이터베이스에서 귀하의 계정과 정보를 비활성화하거나 삭제합니다. 다만 당사는 사기 방지, 문제 해결, 조사 지원, 당사 법적 약관의 집행 및/또는 관련 법적 요건 준수를 위해 일부 정보를 당사 파일에 보관할 수 있습니다.',
          '**위치 데이터:** 귀하는 기기 설정 또는 앱 내에서 언제든지 위치 추적을 비활성화할 수 있습니다. 다만 위치 추적을 비활성화하면 자동 여행 기록이라는 핵심 기능이 작동하지 않습니다.',
          `개인정보 보호 권리에 관한 질문이나 의견이 있으시면 ${EMAIL}로 이메일을 보내 주십시오.`
        ]
      },
      {
        id: 'dnt',
        heading: '11. 추적 금지(Do-Not-Track) 기능에 대한 제어',
        blocks: [
          '대부분의 웹 브라우저와 일부 모바일 운영체제 및 모바일 애플리케이션에는 귀하의 온라인 브라우징 활동에 관한 데이터가 모니터링되거나 수집되지 않기를 바라는 개인정보 보호 선호를 알리기 위해 활성화할 수 있는 추적 금지(“DNT”) 기능 또는 설정이 포함되어 있습니다. 현재 DNT 신호를 인식하고 구현하기 위한 통일된 기술 표준은 확정되지 않았습니다. 따라서 당사는 현재 DNT 브라우저 신호나 온라인에서 추적되지 않겠다는 귀하의 선택을 자동으로 전달하는 기타 메커니즘에 응답하지 않습니다. 향후 당사가 따라야 하는 온라인 추적 표준이 채택되는 경우, 당사는 본 개인정보 처리방침의 개정판을 통해 해당 관행을 귀하에게 알려 드리겠습니다.'
        ]
      },
      {
        id: 'us-residents',
        heading: '12. 미국 거주자에게는 특정한 개인정보 보호 권리가 있습니까?',
        blocks: [
          { inShort: '귀하가 캘리포니아, 콜로라도, 코네티컷, 유타 또는 버지니아 거주자인 경우, 귀하에게는 개인정보 접근과 관련한 특정 권리가 부여됩니다.' },
          { h3: '당사는 어떤 범주의 개인정보를 수집합니까?' },
          '당사는 지난 십이(12)개월 동안 다음 범주의 개인정보를 수집했습니다.',
          {
            table: {
              head: ['범주', '예시', '수집 여부'],
              rows: [
                ['A. 식별자', '실명, 별칭, 이메일 주소, 계정 이름, 고유 식별자 등의 연락처 정보', '예'],
                ['B. 캘리포니아 고객 기록법(California Customer Records statute)에 정의된 개인정보', '이름, 연락처 정보', '예'],
                ['C. 주법 또는 연방법상 보호되는 분류 특성', '성별 및 생년월일', '아니요'],
                ['D. 상업 정보', '거래 정보, 구매 내역, 금융 정보 및 결제 정보', '아니요'],
                ['E. 생체 정보', '지문 및 성문', '아니요'],
                ['F. 인터넷 또는 기타 유사한 네트워크 활동', '브라우징 기록, 검색 기록, 온라인 행동, 관심사 데이터 및 당사 애플리케이션과의 상호작용', '예'],
                ['G. 위치 정보', '백그라운드 추적이 활성화된 경우 지속적으로 추적되는 정밀한 기기 위치', '예'],
                ['H. 오디오, 전자, 시각, 열, 후각 또는 이와 유사한 정보', '여행 추억을 만들기 위해 사용자가 업로드한 사진', '예'],
                ['I. 직업 또는 고용 관련 정보', '업무용 연락처 정보', '아니요'],
                ['J. 교육 정보', '학생 기록 및 디렉터리 정보', '아니요'],
                ['K. 수집된 개인정보에서 도출된 추론', 'AI 추천을 위해 선호, 특성 및 여행 패턴에 관한 프로필을 만들고자 수집된 정보에서 도출한 추론', '예'],
                ['L. 민감한 개인정보', '정밀한 위치 정보', '예']
              ]
            }
          },
          '당사는 서비스 제공에 필요한 범위에서, 또는 다음 기간 동안 수집한 개인정보를 이용하고 보관합니다.',
          [
            '범주 A - 사용자가 당사에 계정을 보유하는 동안',
            '범주 B - 사용자가 당사에 계정을 보유하는 동안',
            '범주 F - 사용자가 당사에 계정을 보유하는 동안',
            '범주 G - 사용자가 당사에 계정을 보유하는 동안',
            '범주 H - 사용자가 당사에 계정을 보유하는 동안',
            '범주 K - 사용자가 당사에 계정을 보유하는 동안',
            '범주 L - 사용자가 당사에 계정을 보유하는 동안'
          ],
          '당사는 또한 다음과 관련하여 귀하가 대면, 온라인, 전화 또는 우편으로 당사와 상호작용하는 경우를 통해 이러한 범주 이외의 기타 개인정보를 수집할 수 있습니다.',
          [
            '고객 지원 채널을 통해 도움을 받는 경우;',
            '고객 설문조사 또는 경품 행사에 참여하는 경우; 및',
            '당사 서비스 제공을 원활히 하고 귀하의 문의에 응답하는 경우.'
          ],
          { h3: '당사는 귀하의 개인정보를 어떻게 이용하고 공유합니까?' },
          '당사가 귀하의 개인정보를 이용하는 방법은 “[당사는 귀하의 정보를 어떻게 처리합니까?](#process)” 섹션에서 확인하십시오.',
          { h3: '귀하의 정보가 다른 누군가와 공유됩니까?' },
          '당사는 당사와 각 서비스 제공업체 간의 서면 계약에 따라 서비스 제공업체에 귀하의 개인정보를 공개할 수 있습니다. 당사가 개인정보를 공개하는 방법에 관한 자세한 내용은 “[당사는 언제, 누구와 귀하의 개인정보를 공유합니까?](#sharing)” 섹션에서 확인하십시오.',
          '당사는 기술 개발 및 시연을 위한 내부 연구 수행 등 당사 자체의 사업 목적으로 귀하의 개인정보를 이용할 수 있습니다. 이는 귀하의 개인정보 “판매”로 간주되지 않습니다.',
          '당사는 지난 십이(12)개월 동안 사업적 또는 상업적 목적으로 제3자에게 개인정보를 판매하거나 공유한 적이 없습니다. 당사는 지난 십이(12)개월 동안 사업적 또는 상업적 목적으로 다음 범주의 개인정보를 제3자에게 공개했습니다.',
          [
            '범주 A. 식별자',
            '범주 B. 캘리포니아 고객 기록법(California Customer Records law)에 정의된 개인정보',
            '범주 G. 위치 정보',
            '범주 H. 오디오, 전자, 시각 및 이와 유사한 정보',
            '범주 K. 수집된 개인정보에서 도출된 추론',
            '범주 L. 민감한 개인정보(위치 정보)'
          ],
          '당사가 사업적 또는 상업적 목적으로 개인정보를 공개한 제3자의 범주는 “[당사는 언제, 누구와 귀하의 개인정보를 공유합니까?](#sharing)”에서 확인할 수 있습니다.',
          { h3: '캘리포니아 거주자' },
          '“Shine The Light” 법으로도 알려진 캘리포니아 민법(California Civil Code) 제1798.83조는 캘리포니아 거주자인 당사 사용자가 연 1회 무료로, 직전 역년에 당사가 직접 마케팅 목적으로 제3자에게 공개한 개인정보의 범주(있는 경우)와 당사가 개인정보를 공유한 모든 제3자의 이름 및 주소에 관한 정보를 당사에 요청하여 받을 수 있도록 허용합니다. 귀하가 캘리포니아 거주자로서 이러한 요청을 하고자 하는 경우, 아래에 제공된 연락처 정보를 이용하여 서면으로 당사에 요청을 제출해 주십시오.',
          '귀하가 18세 미만이고 캘리포니아에 거주하며 서비스에 등록된 계정을 보유하고 있는 경우, 귀하는 서비스에 공개적으로 게시한 원치 않는 데이터의 삭제를 요청할 권리가 있습니다. 해당 데이터의 삭제를 요청하려면 아래에 제공된 연락처 정보를 이용하여 당사에 연락하시고, 귀하의 계정과 연결된 이메일 주소와 귀하가 캘리포니아에 거주한다는 진술을 포함해 주십시오. 당사는 해당 데이터가 서비스에 공개적으로 표시되지 않도록 하겠지만, 해당 데이터가 당사의 모든 시스템(예: 백업 등)에서 완전히 또는 포괄적으로 삭제되지 않을 수 있다는 점에 유의하십시오.',
          { h3: 'CCPA 개인정보 보호 고지' },
          '이 섹션은 캘리포니아 거주자에게만 적용됩니다. 캘리포니아 소비자 개인정보 보호법(CCPA)에 따라 귀하는 아래에 나열된 권리를 가집니다.',
          '캘리포니아 규정집(California Code of Regulations)은 “거주자”를 다음과 같이 정의합니다.',
          [
            '(1) 일시적이거나 경과적인 목적 이외의 목적으로 캘리포니아주에 있는 모든 개인, 그리고',
            '(2) 캘리포니아주에 주소지를 두고 있으나 일시적이거나 경과적인 목적으로 캘리포니아주 밖에 있는 모든 개인'
          ],
          '그 밖의 모든 개인은 “비거주자”로 정의됩니다.',
          '이 “거주자”의 정의가 귀하에게 적용되는 경우, 당사는 귀하의 개인정보와 관련하여 특정 권리와 의무를 준수해야 합니다.',
          { h3: '귀하의 개인 데이터에 관한 권리' },
          '**데이터 삭제를 요청할 권리 — 삭제 요청**',
          '귀하는 귀하의 개인정보 삭제를 요청할 수 있습니다. 귀하가 당사에 개인정보 삭제를 요청하면, 당사는 법률이 정한 특정 예외에 따라 귀하의 요청을 존중하여 귀하의 개인정보를 삭제합니다. 이러한 예외에는 다른 소비자의 표현의 자유 행사, 법적 의무에 따른 당사의 준수 요건, 불법 행위로부터 보호하기 위해 필요할 수 있는 처리 등이 포함되나 이에 국한되지 않습니다.',
          '**고지받을 권리 — 알 권리 요청**',
          '상황에 따라 귀하는 다음을 알 권리가 있습니다.',
          [
            '당사가 귀하의 개인정보를 수집하고 이용하는지 여부;',
            '당사가 수집하는 개인정보의 범주;',
            '수집된 개인정보가 이용되는 목적;',
            '당사가 제3자에게 개인정보를 판매하거나 공유하는지 여부;',
            '당사가 사업 목적으로 판매, 공유 또는 공개한 개인정보의 범주;',
            '사업 목적으로 개인정보가 판매, 공유 또는 공개된 제3자의 범주;',
            '개인정보를 수집, 판매 또는 공유하는 사업적 또는 상업적 목적; 그리고',
            '당사가 귀하에 관해 수집한 구체적인 개인정보 항목.'
          ],
          '관련 법률에 따라, 당사는 소비자 요청에 응하여 비식별화된 소비자 정보를 제공하거나 삭제할 의무가 없으며, 소비자 요청을 확인하기 위해 개별 데이터를 재식별할 의무도 없습니다.',
          '**소비자의 개인정보 보호 권리 행사에 대한 차별 금지 권리**',
          '귀하가 개인정보 보호 권리를 행사하더라도 당사는 귀하를 차별하지 않습니다.',
          '**민감한 개인정보의 이용 및 공개를 제한할 권리**',
          '귀하가 캘리포니아 거주자인 경우, 귀하는 민감한 개인정보의 이용 및 공개를 평균적인 소비자가 합리적으로 기대하는 서비스를 수행하거나 상품을 제공하는 데 필요한 범위로만 제한할 권리가 있습니다. 당사는 현재 민감한 개인정보(정밀한 위치 정보)를 앱의 주된 기대 기능인 여행 기록 서비스 제공이라는 핵심 목적으로만 이용합니다.',
          '**본인 확인 절차**',
          '귀하의 요청을 받으면, 당사는 귀하가 당사 시스템에 보유된 정보의 주체와 동일인인지 판단하기 위해 귀하의 신원을 확인해야 합니다. 이러한 확인을 위해 당사는 귀하가 이전에 당사에 제공한 정보와 대조할 수 있도록 귀하에게 정보 제공을 요청해야 합니다. 예를 들어, 귀하가 제출하는 요청의 유형에 따라 당사는 귀하가 제공하는 정보를 당사가 이미 보유하고 있는 정보와 대조할 수 있도록 특정 정보의 제공을 요청하거나, 귀하가 이전에 당사에 제공한 연락 수단(예: 전화 또는 이메일)을 통해 귀하에게 연락할 수 있습니다. 또한 상황에 따라 다른 확인 방법을 사용할 수도 있습니다.',
          '당사는 귀하의 요청에 포함된 개인정보를 귀하의 신원 또는 요청 권한을 확인하는 데만 사용합니다. 당사는 가능한 한 확인을 목적으로 귀하에게 추가 정보를 요청하지 않도록 하겠습니다. 다만 당사가 이미 보유하고 있는 정보로 귀하의 신원을 확인할 수 없는 경우, 신원 확인 및 보안 또는 사기 방지 목적으로 추가 정보 제공을 요청할 수 있습니다. 당사는 귀하의 확인을 마치는 즉시 이렇게 추가로 제공된 정보를 삭제합니다.',
          '**기타 개인정보 보호 권리**',
          [
            '귀하는 귀하의 개인정보 처리에 반대할 수 있습니다.',
            '귀하는 개인 데이터가 부정확하거나 더 이상 관련이 없는 경우 정정을 요청하거나, 해당 정보의 처리 제한을 요청할 수 있습니다.',
            '귀하는 귀하를 대신하여 CCPA에 따른 요청을 하도록 권한 있는 대리인을 지정할 수 있습니다. 당사는 CCPA에 따라 귀하를 대신하여 행동할 정당한 권한을 부여받았다는 증빙을 제출하지 않는 권한 있는 대리인의 요청을 거부할 수 있습니다.',
            '귀하는 향후 귀하의 개인정보를 제3자에게 판매하거나 공유하는 것에 대한 거부(옵트아웃)를 요청할 수 있습니다. 거부 요청을 받으면, 당사는 가능한 한 신속하게, 늦어도 요청 제출일로부터 십오(15)일 이내에 요청에 따라 조치합니다.'
          ],
          `이러한 권리를 행사하려면 ${EMAIL}로 이메일을 보내거나 본 문서 하단의 연락처를 참조하여 당사에 연락할 수 있습니다. 당사의 데이터 처리 방식에 대해 불만이 있으시면 당사에 알려 주시기 바랍니다.`
        ]
      },
      {
        id: 'updates',
        heading: '13. 당사는 본 방침을 업데이트합니까?',
        blocks: [
          { inShort: '예, 당사는 관련 법률을 계속 준수하기 위해 필요에 따라 본 방침을 업데이트합니다.' },
          '당사는 수시로 본 개인정보 처리방침을 업데이트할 수 있습니다. 업데이트된 버전은 갱신된 “최종 업데이트” 날짜로 표시되며, 업데이트된 버전은 열람 가능해지는 즉시 효력이 발생합니다. 본 개인정보 처리방침을 중대하게 변경하는 경우, 당사는 변경 사항에 관한 고지를 눈에 띄게 게시하거나 귀하에게 직접 알림을 보내는 방식으로 귀하에게 알릴 수 있습니다. 당사가 귀하의 정보를 어떻게 보호하고 있는지 확인하기 위해 본 개인정보 처리방침을 자주 검토하실 것을 권장합니다.'
        ]
      },
      {
        id: 'contact',
        heading: '14. 본 방침에 관해 어떻게 문의할 수 있습니까?',
        blocks: [`본 방침에 관한 질문이나 의견이 있으시면 ${EMAIL}로 이메일을 보내 주십시오.`]
      },
      {
        id: 'review',
        heading: '15. 당사가 귀하로부터 수집한 데이터는 어떻게 검토, 업데이트 또는 삭제할 수 있습니까?',
        blocks: [
          `귀하 국가의 관련 법률에 따라, 귀하는 당사가 귀하로부터 수집한 개인정보에 대한 접근, 해당 정보의 변경 또는 삭제를 요청할 권리가 있을 수 있습니다. 귀하의 개인정보 검토, 업데이트 또는 삭제를 요청하려면 ${EMAIL}로 이메일을 보내 주십시오.`,
          '또한 다음과 같은 방법으로 Travo 앱 내에서 직접 데이터를 관리할 수 있습니다.',
          [
            '계정 설정에 접속하여 프로필 정보 업데이트',
            '개별 여행 추억 또는 사진 삭제',
            '앱 설정 또는 기기 설정에서 위치 추적 비활성화',
            '계정 전체 삭제 요청(90일 이내에 귀하의 모든 데이터가 삭제됨)'
          ],
          '본 개인정보 처리방침은 귀하에게 자동화되고 지능적인 여행 기록 경험을 제공하는 동시에, Travo가 귀하의 여행 데이터를 어떻게 처리하는지 투명하게 알리기 위해 작성되었습니다. 당사가 귀하의 개인정보를 어떻게 보호하는지에 관해 질문이 있으시면 언제든지 편하게 문의해 주십시오.'
        ]
      }
    ]
  },

  terms: {
    title: '이용약관',
    updated: '최종 업데이트: 2026년 10월 25일',
    seo: {
      title: '이용약관 | Travo',
      description:
        'Travo 여행 기록 앱과 관련 서비스 이용에 관한 법적 약관입니다. 계정, 콘텐츠, 허용되는 이용 방식, 당사의 책임에 대해 안내합니다.',
      keywords: 'Travo 이용약관, 서비스 약관, 법적 약관, Travo 앱 라이선스'
    },
    sections: [
      {
        id: 'agreement',
        heading: '본 약관에 대한 동의',
        blocks: [
          `당사는 Travo Labs라는 이름으로 운영되는 ${LEGAL_NAME}입니다(“**회사**” 또는 “**당사**”).`,
          '당사는 Travo 모바일 애플리케이션과 함께, 본 법적 약관(“**본 약관**”)을 참조하거나 이에 링크된 기타 관련 제품 및 서비스(총칭하여 “**서비스**”)를 운영합니다.',
          'Travo는 귀하의 여행 이력을 자동으로 기록하고 정리하는 지능형 여행 기록 앱입니다. 앱은 백그라운드에서 귀하의 위치(도시 및 국가)를 자동으로 기록하고, 귀하의 여행 데이터를 분석하며, 사진으로 추억을 만들고, 귀하의 여행 패턴에 기반한 AI 기반 여행지 추천을 제공합니다.',
          `${EMAIL}로 이메일을 보내 당사에 연락할 수 있습니다.`,
          '본 약관은 개인으로서 또는 법인을 대신하여 행위하는 귀하(“**귀하**”)와 Travo Labs 간에 귀하의 서비스 접근 및 이용에 관하여 체결되는 법적 구속력이 있는 계약입니다. 귀하는 서비스에 접근함으로써 본 약관을 모두 읽고 이해했으며 이에 구속되는 것에 동의했음을 인정합니다. 본 약관 전체에 동의하지 않는 경우, 귀하는 서비스 이용이 명시적으로 금지되며 즉시 이용을 중단해야 합니다.',
          '서비스에 수시로 게시될 수 있는 추가 이용 조건 또는 문서는 이로써 본 약관에 참조로써 명시적으로 포함됩니다. 당사는 단독 재량에 따라 언제든지, 어떠한 이유로든 본 약관을 변경하거나 수정할 권리를 보유합니다. 당사는 본 약관의 “최종 업데이트” 날짜를 갱신하여 변경 사항을 알려 드리며, 귀하는 각 변경 사항에 대해 개별 통지를 받을 권리를 포기합니다. 업데이트 내용을 확인하기 위해 본 약관을 정기적으로 검토하는 것은 귀하의 책임입니다. 개정된 약관이 게시된 날 이후에도 서비스를 계속 이용하는 경우, 귀하는 개정된 약관의 변경 사항을 적용받으며, 해당 변경 사항을 인지하고 수락한 것으로 간주됩니다.',
          '기록 보관을 위해 본 약관의 사본을 출력해 두실 것을 권장합니다.'
        ]
      },
      {
        id: 'services',
        heading: '1. 당사의 서비스',
        blocks: [
          '서비스 이용 시 제공되는 정보는 그 배포나 이용이 법률이나 규정에 위배되거나 당사가 해당 관할권 또는 국가에서 등록 요건의 적용을 받게 되는 관할권 또는 국가의 개인이나 법인에게 배포하거나 그들이 이용하도록 하기 위한 것이 아닙니다. 따라서 다른 지역에서 서비스에 접근하기로 선택하는 사람은 자신의 판단에 따라 그렇게 하는 것이며, 현지 법률이 적용되는 경우 그 범위 내에서 현지 법률을 준수할 책임은 전적으로 해당 사람에게 있습니다.',
          '서비스는 귀하가 여행 이력을 자동으로 기록하고 정리할 수 있도록 개인적·비상업적 용도로 제공됩니다. 서비스는 귀하가 방문한 도시와 나라를 기록하기 위해 귀하 기기의 위치 서비스를 사용합니다.'
        ]
      },
      {
        id: 'ip',
        heading: '2. 지식재산권',
        blocks: [
          { h3: '당사의 지식재산' },
          '당사는 서비스 내 모든 소스 코드, 데이터베이스, 기능, 소프트웨어, 애플리케이션 디자인, 오디오, 비디오, 텍스트, 사진 및 그래픽(총칭하여 “**콘텐츠**”)과 그에 포함된 상표, 서비스표 및 로고(“**마크**”)를 포함하여 당사 서비스의 모든 지식재산권의 소유자 또는 라이선스 보유자입니다.',
          '당사의 콘텐츠와 마크는 미국 및 전 세계의 저작권법과 상표법(및 기타 다양한 지식재산권법과 부정경쟁방지법) 및 조약에 의해 보호됩니다.',
          '콘텐츠와 마크는 귀하의 개인적·비상업적 용도로만 서비스 내에서 또는 서비스를 통해 “있는 그대로” 제공됩니다.',
          { h3: '귀하의 서비스 이용' },
          '귀하가 아래 “[금지 행위](#prohibited)” 섹션을 포함하여 본 약관을 준수하는 것을 조건으로, 당사는 귀하에게 다음에 대한 비독점적이고, 양도 불가능하며, 철회 가능한 라이선스를 부여합니다.',
          ['서비스에 접근하는 것, 그리고', '귀하가 적법하게 접근 권한을 얻은 콘텐츠의 일부를 다운로드하거나 그 사본을 출력하는 것.'],
          '이는 오로지 귀하의 개인적·비상업적 용도에 한합니다.',
          '본 섹션 또는 본 약관의 다른 부분에 명시된 경우를 제외하고, 서비스의 어떠한 부분이나 콘텐츠 또는 마크도 당사의 명시적인 사전 서면 허가 없이 어떠한 상업적 목적으로든 복사, 복제, 수집, 재게시, 업로드, 게시, 공개 전시, 인코딩, 번역, 전송, 배포, 판매, 라이선스 부여 또는 기타 방식으로 이용될 수 없습니다.',
          `본 섹션 또는 본 약관의 다른 부분에 명시된 것 이외의 방식으로 서비스, 콘텐츠 또는 마크를 이용하고자 하는 경우, ${EMAIL}로 요청해 주십시오. 당사가 귀하에게 서비스 또는 콘텐츠의 일부를 게시, 복제 또는 공개 전시할 수 있는 허가를 부여하는 경우, 귀하는 당사를 서비스, 콘텐츠 또는 마크의 소유자 또는 라이선스 제공자로 표시해야 하며, 당사의 콘텐츠를 게시, 복제 또는 전시할 때 모든 저작권 또는 소유권 고지가 표시되거나 보이도록 해야 합니다.`,
          '당사는 서비스, 콘텐츠 및 마크에 관하여 귀하에게 명시적으로 부여하지 않은 모든 권리를 보유합니다.',
          '이러한 지식재산권을 침해하는 경우 본 약관의 중대한 위반에 해당하며, 귀하의 서비스 이용 권리는 즉시 종료됩니다.',
          { h3: '귀하의 제출물 및 사용자 콘텐츠' },
          '서비스를 이용하기 전에 본 섹션과 “[금지 행위](#prohibited)” 섹션을 주의 깊게 검토하여, 귀하가 서비스를 통해 콘텐츠를 게시하거나 업로드할 때 (a) 귀하가 당사에 부여하는 권리와 (b) 귀하가 부담하는 의무를 이해하시기 바랍니다.',
          '**제출물:** 서비스에 관한 질문, 의견, 제안, 아이디어, 피드백 또는 기타 정보(“**제출물**”)를 당사에 직접 보냄으로써, 귀하는 해당 제출물에 대한 모든 지식재산권을 당사에 양도하는 데 동의합니다. 귀하는 당사가 해당 제출물을 소유하며, 귀하에게 출처를 표시하거나 보상하지 않고도 상업적이든 아니든 모든 적법한 목적을 위해 이를 제한 없이 이용하고 배포할 권리를 가진다는 데 동의합니다.',
          '**귀하가 게시하거나 업로드하는 내용에 대한 책임은 귀하에게 있습니다:** 서비스의 어느 부분을 통해서든 당사에 제출물을 보냄으로써 귀하는 다음과 같이 합니다.',
          [
            '당사의 “[금지 행위](#prohibited)”를 읽고 이에 동의했으며, 불법적이거나, 괴롭히거나, 혐오스럽거나, 유해하거나, 명예를 훼손하거나, 음란하거나, 따돌리거나, 모욕적이거나, 차별적이거나, 개인 또는 집단을 위협하거나, 성적으로 노골적이거나, 허위이거나, 부정확하거나, 기만적이거나, 오해의 소지가 있는 제출물을 서비스를 통해 게시, 전송, 공개, 업로드 또는 송신하지 않을 것임을 확인합니다;',
            '관련 법률이 허용하는 범위 내에서 해당 제출물에 대한 모든 저작인격권을 포기합니다;',
            '해당 제출물이 귀하의 독창적인 것이거나 귀하가 해당 제출물을 제출하는 데 필요한 권리와 라이선스를 보유하고 있으며, 귀하의 제출물과 관련하여 위에서 언급한 권리를 당사에 부여할 완전한 권한이 있음을 보증합니다; 그리고',
            '귀하의 제출물이 기밀 정보에 해당하지 않음을 보증하고 진술합니다.'
          ],
          '귀하는 귀하의 제출물에 대해 전적인 책임을 지며, 귀하가 (a) 본 섹션, (b) 제3자의 지식재산권 또는 (c) 관련 법률을 위반하여 당사가 입을 수 있는 모든 손실을 당사에 배상하는 데 명시적으로 동의합니다.'
        ]
      },
      {
        id: 'representations',
        heading: '3. 사용자의 진술',
        blocks: [
          '서비스를 이용함으로써 귀하는 다음을 진술하고 보증합니다: (1) 귀하가 제출하는 모든 등록 정보는 사실이고 정확하며 최신이고 완전합니다; (2) 귀하는 해당 정보의 정확성을 유지하고 필요에 따라 등록 정보를 신속히 업데이트합니다; (3) 귀하는 법적 행위능력이 있으며 본 약관을 준수하는 데 동의합니다; (4) 귀하는 귀하가 거주하는 관할권에서 미성년자가 아닙니다; (5) 귀하는 봇, 스크립트 또는 기타 방식을 통한 자동화된 수단이나 사람이 아닌 수단으로 서비스에 접근하지 않습니다; (6) 귀하는 불법적이거나 승인되지 않은 목적으로 서비스를 이용하지 않습니다; 그리고 (7) 귀하의 서비스 이용은 관련 법률이나 규정을 위반하지 않습니다.',
          '귀하가 사실이 아니거나, 부정확하거나, 최신이 아니거나, 불완전한 정보를 제공하는 경우, 당사는 귀하의 계정을 정지하거나 해지하고, 서비스(또는 그 일부)에 대한 현재 또는 향후의 모든 이용을 거부할 권리가 있습니다.'
        ]
      },
      {
        id: 'registration',
        heading: '4. 사용자 등록',
        blocks: [
          '서비스를 이용하려면 등록이 필요할 수 있습니다. 귀하는 비밀번호를 기밀로 유지하는 데 동의하며, 귀하의 계정과 비밀번호의 모든 이용에 대해 책임을 집니다. 당사는 귀하가 선택한 사용자 이름이 부적절하거나, 음란하거나, 기타 불쾌하다고 당사가 단독 재량으로 판단하는 경우 해당 사용자 이름을 삭제, 회수 또는 변경할 권리를 보유합니다.'
        ]
      },
      {
        id: 'prohibited',
        heading: '5. 금지 행위',
        blocks: [
          '귀하는 당사가 서비스를 제공하는 목적 이외의 목적으로 서비스에 접근하거나 이를 이용할 수 없습니다. 서비스는 당사가 특별히 보증하거나 승인한 경우를 제외하고 어떠한 상업적 활동과 관련해서도 이용될 수 없습니다.',
          '서비스의 사용자로서 귀하는 다음 행위를 하지 않는 데 동의합니다.',
          [
            '당사의 서면 허가 없이, 직간접적으로 컬렉션, 편집물, 데이터베이스 또는 디렉터리를 만들거나 편찬하기 위해 서비스에서 데이터나 기타 콘텐츠를 체계적으로 수집하는 행위.',
            '특히 사용자 비밀번호와 같은 민감한 계정 정보를 알아내려는 시도 등으로 당사 및 다른 사용자를 속이거나, 사기를 치거나, 오도하는 행위.',
            '콘텐츠의 이용이나 복사를 방지 또는 제한하거나 서비스 및/또는 그에 포함된 콘텐츠의 이용에 대한 제한을 시행하는 기능을 포함하여, 서비스의 보안 관련 기능을 우회, 비활성화 또는 기타 방식으로 방해하는 행위.',
            '당사가 보기에 당사 및/또는 서비스를 비방하거나, 평판을 손상하거나, 기타 방식으로 해를 끼치는 행위.',
            '다른 사람을 괴롭히거나, 학대하거나, 해를 끼치기 위해 서비스에서 얻은 정보를 이용하는 행위.',
            '당사의 지원 서비스를 부적절하게 이용하거나 남용 또는 위법 행위에 대한 허위 신고를 제출하는 행위.',
            '관련 법률이나 규정에 부합하지 않는 방식으로 서비스를 이용하는 행위.',
            '서비스에 대한 무단 프레이밍 또는 링크를 하는 행위.',
            '누구든 서비스를 중단 없이 이용하고 누리는 것을 방해하거나, 서비스의 이용, 특징, 기능, 운영 또는 유지보수를 수정, 손상, 방해, 변경 또는 간섭하는 바이러스, 트로이 목마 또는 기타 자료(대문자의 과도한 사용 및 스팸(반복적인 텍스트의 지속적 게시) 포함)를 업로드 또는 전송하거나 업로드 또는 전송을 시도하는 행위.',
            '스크립트를 사용하여 댓글이나 메시지를 보내거나, 데이터 마이닝, 로봇 또는 유사한 데이터 수집 및 추출 도구를 사용하는 등 시스템을 자동화된 방식으로 이용하는 행위.',
            '콘텐츠에서 저작권 또는 기타 소유권 고지를 삭제하는 행위.',
            '다른 사용자나 사람을 사칭하려 하거나 다른 사용자의 사용자 이름을 사용하는 행위.',
            '투명 GIF(“gifs”), 1×1 픽셀, 웹 버그, 쿠키 또는 기타 유사한 장치(“스파이웨어”, “수동 수집 메커니즘” 또는 “pcms”라고도 함)를 포함하되 이에 국한되지 않는, 수동적 또는 능동적 정보 수집 또는 전송 메커니즘으로 작동하는 자료를 업로드 또는 전송하거나 업로드 또는 전송을 시도하는 행위.',
            '서비스 또는 서비스에 연결된 네트워크나 서비스를 방해하거나, 중단시키거나, 이에 과도한 부담을 주는 행위.',
            '귀하에게 서비스의 일부를 제공하는 데 관여하는 당사의 직원이나 대리인을 괴롭히거나, 성가시게 하거나, 겁을 주거나, 위협하는 행위.',
            '서비스 또는 서비스의 일부에 대한 접근을 방지하거나 제한하기 위해 마련된 서비스의 조치를 우회하려고 시도하는 행위.',
            'Flash, PHP, HTML, JavaScript 또는 기타 코드를 포함하되 이에 국한되지 않는 서비스의 소프트웨어를 복사하거나 개작하는 행위.',
            '관련 법률이 허용하는 경우를 제외하고, 서비스를 구성하거나 어떠한 방식으로든 서비스의 일부를 이루는 소프트웨어를 해독, 디컴파일, 디스어셈블 또는 리버스 엔지니어링하는 행위.',
            '표준 검색 엔진 또는 인터넷 브라우저 사용의 결과인 경우를 제외하고, 서비스에 접근하는 스파이더, 로봇, 치트 유틸리티, 스크레이퍼 또는 오프라인 리더를 포함하되 이에 국한되지 않는 자동화 시스템을 사용, 실행, 개발 또는 배포하거나, 승인되지 않은 스크립트나 기타 소프트웨어를 사용 또는 실행하는 행위.',
            '서비스에서 구매하기 위해 구매 대행인 또는 구매 대리인을 이용하는 행위.',
            '원치 않는 이메일을 보낼 목적으로 전자적 또는 기타 수단을 통해 사용자의 사용자 이름 및/또는 이메일 주소를 수집하거나, 자동화된 수단으로 또는 허위로 사용자 계정을 만드는 것을 포함하여 서비스를 무단으로 이용하는 행위.',
            '당사와 경쟁하려는 노력의 일환으로 서비스를 이용하거나, 기타 수익 창출 활동이나 상업적 사업을 위해 서비스 및/또는 콘텐츠를 이용하는 행위.',
            '귀하의 여행 통계나 위치 데이터를 조작, 위조 또는 인위적으로 부풀리려고 시도하는 행위.',
            '귀하의 계정 자격 증명을 다른 사람과 공유하거나 다른 사람이 귀하의 계정에 접근하도록 허용하는 행위.'
          ]
        ]
      },
      {
        id: 'contributions',
        heading: '6. 사용자 생성 기여물',
        blocks: [
          '서비스는 사진, 여행 추억, 댓글 및 기타 자료를 포함하되 이에 국한되지 않는 콘텐츠(“**기여물**”)를 업로드, 게시 또는 공유하도록 귀하에게 요청할 수 있습니다. 귀하의 기여물은 서비스의 다른 사용자가 볼 수 있습니다. 귀하가 기여물을 만들거나 제공하는 경우, 귀하는 이로써 다음을 진술하고 보증합니다.',
          [
            '귀하의 기여물의 제작, 배포, 전송, 공개 전시 또는 공연, 그리고 귀하의 기여물에 대한 접근, 다운로드 또는 복사는 제3자의 저작권, 특허, 상표, 영업비밀 또는 저작인격권을 포함하되 이에 국한되지 않는 소유권을 침해하지 않으며 앞으로도 침해하지 않습니다.',
            '귀하는 귀하의 기여물의 창작자이자 소유자이거나, 서비스와 본 약관이 상정하는 모든 방식으로 귀하의 기여물을 이용하고 당사, 서비스 및 서비스의 다른 사용자가 이를 이용하도록 승인하는 데 필요한 라이선스, 권리, 동의, 권리 포기 및 허가를 보유하고 있습니다.',
            '귀하는 서비스와 본 약관이 상정하는 모든 방식으로 귀하의 기여물을 포함하고 이용할 수 있도록, 귀하의 기여물에 등장하는 식별 가능한 모든 개인 각각으로부터 그 이름이나 초상을 이용하는 데 대한 서면 동의, 권리 포기 및/또는 허가를 받았습니다.',
            '귀하의 기여물은 허위이거나, 부정확하거나, 오해의 소지가 있지 않습니다.',
            '귀하의 기여물은 원치 않거나 승인되지 않은 광고, 홍보물, 피라미드 방식, 행운의 편지, 스팸, 대량 메일 또는 기타 형태의 권유가 아닙니다.',
            '귀하의 기여물은 음란하거나, 외설적이거나, 선정적이거나, 불결하거나, 폭력적이거나, 괴롭히거나, 문서에 의한 명예훼손이거나, 구두에 의한 명예훼손이거나, 기타 (당사가 판단하기에) 불쾌하지 않습니다.',
            '귀하의 기여물은 누구도 조롱하거나, 놀리거나, 비하하거나, 위협하거나, 학대하지 않습니다.',
            '귀하의 기여물은 다른 사람을 (해당 용어의 법적 의미에서) 괴롭히거나 위협하는 데 이용되지 않으며, 특정인 또는 특정 집단에 대한 폭력을 조장하는 데 이용되지 않습니다.',
            '귀하의 기여물은 관련 법률, 규정 또는 규칙을 위반하지 않습니다.',
            '귀하의 기여물은 제3자의 프라이버시권 또는 퍼블리시티권을 침해하지 않습니다.',
            '귀하의 기여물은 아동 포르노에 관한 관련 법률이나 기타 미성년자의 건강 또는 복지를 보호하기 위한 법률을 위반하지 않습니다.',
            '귀하의 기여물에는 인종, 출신 국가, 성별, 성적 선호 또는 신체장애와 관련된 모욕적인 발언이 포함되어 있지 않습니다.',
            '귀하의 기여물은 본 약관의 조항이나 관련 법률 또는 규정을 기타 방식으로 위반하지 않으며, 이를 위반하는 자료에 링크하지 않습니다.'
          ],
          '앞서 말한 사항을 위반하여 서비스를 이용하는 것은 본 약관을 위반하는 것이며, 특히 귀하의 서비스 이용 권리가 해지되거나 정지될 수 있습니다.'
        ]
      },
      {
        id: 'contribution-license',
        heading: '7. 기여물 라이선스',
        blocks: [
          '귀하는 서비스의 어느 부분에든 기여물을 게시함으로써, 상업적, 광고 또는 기타 어떠한 목적으로든 해당 기여물(귀하의 이미지와 음성을 포함하되 이에 국한되지 않음)을 호스팅, 이용, 복사, 복제, 공개, 판매, 재판매, 출판, 방송, 제목 변경, 보관, 저장, 캐시, 공개 공연, 공개 전시, 형식 변경, 번역, 전송, 발췌(전체 또는 일부)하고 배포하며, 해당 기여물의 2차적 저작물을 작성하거나 이를 다른 저작물에 포함하고, 앞서 말한 사항에 대한 서브라이선스를 부여 및 승인할 수 있는 제한 없고, 무제한적이며, 취소 불가능하고, 영구적이며, 비독점적이고, 양도 가능하며, 로열티가 없고, 대가가 완납된 전 세계적 권리 및 라이선스를 당사에 자동으로 부여하며, 귀하가 이를 부여할 권리가 있음을 진술하고 보증합니다. 이용 및 배포는 모든 미디어 형식과 모든 미디어 채널을 통해 이루어질 수 있습니다.',
          '이 라이선스는 현재 알려져 있거나 향후 개발될 모든 형식, 미디어 또는 기술에 적용되며, 해당되는 경우 귀하의 이름, 회사명 및 프랜차이즈명과 귀하가 제공하는 모든 상표, 서비스표, 상호, 로고, 개인 및 상업적 이미지를 당사가 이용하는 것을 포함합니다. 귀하는 귀하의 기여물에 대한 모든 저작인격권을 포기하며, 귀하의 기여물에 대해 저작인격권이 달리 주장된 적이 없음을 보증합니다.',
          '당사는 귀하의 기여물에 대해 어떠한 소유권도 주장하지 않습니다. 귀하는 귀하의 모든 기여물과 그에 관련된 모든 지식재산권 또는 기타 소유권에 대한 완전한 소유권을 보유합니다. 당사는 서비스의 어느 영역에서든 귀하가 제공한 기여물에 포함된 진술이나 표현에 대해 책임지지 않습니다. 귀하는 서비스에 대한 귀하의 기여물에 대해 전적인 책임을 지며, 귀하의 기여물과 관련하여 당사의 모든 책임을 면제하고 당사를 상대로 한 어떠한 법적 조치도 삼가는 데 명시적으로 동의합니다.',
          '당사는 단독적이고 절대적인 재량에 따라 (1) 기여물을 편집, 삭제 처리 또는 기타 방식으로 변경하고, (2) 기여물을 서비스 내 더 적절한 위치에 배치하기 위해 재분류하며, (3) 통지 없이 언제든지, 어떠한 이유로든 기여물을 사전 검토하거나 삭제할 권리가 있습니다. 당사는 귀하의 기여물을 모니터링할 의무가 없습니다.'
        ]
      },
      {
        id: 'app-license',
        heading: '8. 모바일 애플리케이션 라이선스',
        blocks: [
          { h3: '이용 라이선스' },
          '귀하가 모바일 애플리케이션을 통해 서비스에 접근하는 경우, 당사는 귀하가 소유하거나 관리하는 무선 전자 기기에 모바일 애플리케이션을 설치하여 이용하고, 본 약관에 포함된 본 모바일 애플리케이션 라이선스의 이용 조건에 엄격히 따라 해당 기기에서 모바일 애플리케이션에 접근하여 이용할 수 있는 철회 가능하고, 비독점적이며, 양도 불가능한 제한적 권리를 귀하에게 부여합니다. 귀하는 다음 행위를 해서는 안 됩니다.',
          [
            '관련 법률이 허용하는 경우를 제외하고, 애플리케이션을 디컴파일, 리버스 엔지니어링, 디스어셈블하거나, 애플리케이션의 소스 코드를 도출하려고 시도하거나, 애플리케이션을 복호화하는 행위;',
            '애플리케이션을 수정, 개작, 개선, 향상 또는 번역하거나 애플리케이션으로부터 2차적 저작물을 만드는 행위;',
            '애플리케이션에 대한 접근 또는 이용과 관련하여 관련 법률, 규칙 또는 규정을 위반하는 행위;',
            '당사 또는 애플리케이션의 라이선스 제공자가 게시한 소유권 고지(저작권 또는 상표 고지 포함)를 제거, 변경 또는 가리는 행위;',
            '애플리케이션이 설계되거나 의도되지 않은 수익 창출 활동, 상업적 사업 또는 기타 목적으로 애플리케이션을 이용하는 행위;',
            '여러 기기나 사용자가 동시에 접근하거나 이용할 수 있는 네트워크 또는 기타 환경에서 애플리케이션을 이용할 수 있도록 하는 행위;',
            '직간접적으로 애플리케이션과 경쟁하거나 어떠한 방식으로든 애플리케이션을 대체하는 제품, 서비스 또는 소프트웨어를 만드는 데 애플리케이션을 이용하는 행위;',
            '애플리케이션을 이용하여 웹사이트에 자동화된 쿼리를 보내거나 원치 않는 상업용 이메일을 보내는 행위; 또는',
            '애플리케이션과 함께 사용하기 위한 애플리케이션, 액세서리 또는 기기의 설계, 개발, 제조, 라이선스 부여 또는 배포에 당사의 독점 정보, 인터페이스 또는 기타 지식재산을 이용하는 행위.'
          ],
          { h3: 'Apple 및 Android 기기' },
          'App Store 또는 Google Play(각각 “**앱 배포자**”)에서 받은 모바일 애플리케이션을 사용하여 서비스에 접근하는 경우 다음 조건이 적용됩니다.',
          [
            '당사 모바일 애플리케이션에 대해 귀하에게 부여된 라이선스는 해당되는 경우 Apple iOS 또는 Android 운영체제를 사용하는 기기에서, 관련 앱 배포자의 서비스 약관에 명시된 이용 규칙에 따라 애플리케이션을 이용할 수 있는 양도 불가능한 라이선스로 제한됩니다;',
            '당사는 본 약관에 포함된 본 모바일 애플리케이션 라이선스의 이용 조건에 명시되거나 관련 법률에 따라 달리 요구되는 바에 따라 모바일 애플리케이션에 관한 유지보수 및 지원 서비스를 제공할 책임이 있으며, 귀하는 각 앱 배포자가 모바일 애플리케이션에 관한 유지보수 및 지원 서비스를 제공할 의무가 전혀 없음을 인정합니다;',
            '모바일 애플리케이션이 적용 가능한 보증에 부합하지 않는 경우, 귀하는 관련 앱 배포자에게 이를 통지할 수 있으며, 앱 배포자는 자체 약관과 정책에 따라 모바일 애플리케이션에 대해 지불된 구매 가격(있는 경우)을 환불할 수 있고, 관련 법률이 허용하는 최대 범위 내에서 앱 배포자는 모바일 애플리케이션에 관해 그 밖의 어떠한 보증 의무도 지지 않습니다;',
            '귀하는 (i) 미국 정부의 금수 조치 대상 국가 또는 미국 정부가 “테러 지원” 국가로 지정한 국가에 있지 않으며, (ii) 미국 정부의 금지 또는 제한 대상자 명단에 등재되어 있지 않음을 진술하고 보증합니다;',
            '귀하는 모바일 애플리케이션을 이용할 때 적용되는 제3자 계약 조건을 준수해야 합니다. 예를 들어 VoIP 애플리케이션을 사용하는 경우, 모바일 애플리케이션을 이용할 때 해당 무선 데이터 서비스 계약을 위반해서는 안 됩니다; 그리고',
            '귀하는 앱 배포자가 본 약관에 포함된 본 모바일 애플리케이션 라이선스의 이용 조건에 대한 제3자 수익자이며, 각 앱 배포자가 제3자 수익자로서 귀하에 대해 본 약관에 포함된 본 모바일 애플리케이션 라이선스의 이용 조건을 집행할 권리를 가진다는 것(그리고 그 권리를 수락한 것으로 간주된다는 것)을 인정하고 동의합니다.'
          ]
        ]
      },
      {
        id: 'management',
        heading: '9. 서비스 관리',
        blocks: [
          '당사는 다음을 할 권리를 보유하지만 의무는 없습니다: (1) 본 약관 위반 여부를 확인하기 위해 서비스를 모니터링하는 것; (2) 당사의 단독 재량에 따라 법률 또는 본 약관을 위반한 자에 대해, 해당 사용자를 법 집행 기관에 신고하는 것을 포함하되 이에 국한되지 않는 적절한 법적 조치를 취하는 것; (3) 당사의 단독 재량에 따라 제한 없이 귀하의 기여물 또는 그 일부를 거부하거나, 이에 대한 접근을 제한하거나, 그 이용 가능성을 제한하거나, (기술적으로 가능한 범위 내에서) 비활성화하는 것; (4) 당사의 단독 재량에 따라 제한, 통지 또는 책임 없이 크기가 과도하거나 어떠한 방식으로든 당사 시스템에 부담을 주는 모든 파일과 콘텐츠를 서비스에서 제거하거나 기타 방식으로 비활성화하는 것; 그리고 (5) 기타 당사의 권리와 재산을 보호하고 서비스의 적절한 기능을 촉진하도록 설계된 방식으로 서비스를 관리하는 것.'
        ]
      },
      {
        id: 'privacy',
        heading: '10. 개인정보 처리방침',
        blocks: [
          '당사는 데이터 프라이버시와 보안을 중요하게 생각합니다. 당사의 [개인정보 처리방침](/privacy)을 검토하시기 바랍니다. 서비스를 이용함으로써 귀하는 본 약관에 포함된 당사의 개인정보 처리방침에 구속되는 데 동의합니다. 서비스는 미국에서 호스팅된다는 점에 유의하십시오. 개인정보의 수집, 이용 또는 공개를 규율하는 법률이나 기타 요건이 미국의 관련 법률과 다른 세계의 다른 지역에서 서비스에 접근하는 경우, 귀하는 서비스를 계속 이용함으로써 귀하의 데이터를 미국으로 이전하는 것이며, 귀하의 데이터가 미국으로 이전되어 미국에서 처리되는 데 명시적으로 동의합니다.',
          '또한 당사는 아동으로부터 고의로 정보를 수령, 요청 또는 권유하거나 고의로 아동을 대상으로 마케팅하지 않습니다. 따라서 미국 아동 온라인 개인정보 보호법(COPPA)에 따라, 13세 미만의 누군가가 필요하고 검증 가능한 부모의 동의 없이 당사에 개인정보를 제공했다는 사실을 실제로 알게 되는 경우, 당사는 합리적으로 실행 가능한 한 신속하게 해당 정보를 서비스에서 삭제합니다.'
        ]
      },
      {
        id: 'termination',
        heading: '11. 기간 및 해지',
        blocks: [
          '본 약관은 귀하가 서비스를 이용하는 동안 완전한 효력을 유지합니다. 본 약관의 다른 조항을 제한하지 않고, 당사는 단독 재량에 따라 통지나 책임 없이, 본 약관에 포함된 진술, 보증 또는 약정이나 관련 법률 또는 규정의 위반을 포함하되 이에 국한되지 않는 어떠한 이유로든 또는 아무런 이유 없이, 누구에게든 서비스에 대한 접근 및 이용을 거부(특정 IP 주소 차단 포함)할 권리를 보유합니다. 당사는 단독 재량에 따라 경고 없이 언제든지 귀하의 서비스 이용 또는 참여를 종료하거나, 귀하의 계정 및 귀하가 게시한 모든 콘텐츠 또는 정보를 삭제할 수 있습니다.',
          '당사가 어떠한 이유로든 귀하의 계정을 해지하거나 정지하는 경우, 귀하는 제3자를 대신하여 행동하는 경우라도 귀하의 이름, 가명이나 차용한 이름 또는 제3자의 이름으로 새 계정을 등록하고 생성하는 것이 금지됩니다. 당사는 귀하의 계정을 해지하거나 정지하는 것 외에도 민사, 형사 및 금지명령에 의한 구제를 구하는 것을 포함하되 이에 국한되지 않는 적절한 법적 조치를 취할 권리를 보유합니다.'
        ]
      },
      {
        id: 'modifications',
        heading: '12. 변경 및 중단',
        blocks: [
          '당사는 단독 재량에 따라 통지 없이 언제든지 또는 어떠한 이유로든 서비스의 내용을 변경, 수정 또는 삭제할 권리를 보유합니다. 다만 당사는 서비스의 정보를 업데이트할 의무가 없습니다. 당사는 서비스의 수정, 가격 변경, 일시 중지 또는 중단에 대해 귀하나 제3자에게 책임지지 않습니다.',
          '당사는 서비스를 항상 이용할 수 있음을 보장할 수 없습니다. 당사는 하드웨어, 소프트웨어 또는 기타 문제를 겪거나 서비스와 관련된 유지보수를 수행해야 할 수 있으며, 이로 인해 중단, 지연 또는 오류가 발생할 수 있습니다. 당사는 귀하에게 통지하지 않고 언제든지 또는 어떠한 이유로든 서비스를 변경, 개정, 업데이트, 일시 중지, 중단 또는 기타 방식으로 수정할 권리를 보유합니다. 귀하는 서비스의 다운타임 또는 중단 기간 동안 귀하가 서비스에 접근하거나 이를 이용할 수 없어 발생한 손실, 손해 또는 불편에 대해 당사가 어떠한 책임도 지지 않는다는 데 동의합니다. 본 약관의 어떠한 내용도 당사가 서비스를 유지 및 지원하거나 그와 관련한 수정, 업데이트 또는 릴리스를 제공할 의무가 있는 것으로 해석되지 않습니다.'
        ]
      },
      {
        id: 'law',
        heading: '13. 준거법',
        blocks: [
          '본 약관은 대만(중화민국) 법률의 적용을 받습니다. 귀하가 거주하는 국가의 소비자 보호법이 귀하에게 해당 국가에서 소를 제기할 권리를 부여하지 않는 한, 본 약관과 관련하여 발생할 수 있는 모든 분쟁에 대해서는 대만 타이베이 지방법원이 제1심 관할권을 가집니다.'
        ]
      },
      {
        id: 'disputes',
        heading: '14. 분쟁 해결',
        blocks: [
          { h3: '비공식 협상' },
          '귀하 또는 당사(개별적으로 “**당사자**”, 총칭하여 “**양 당사자**”)가 제기하는 본 약관과 관련된 모든 분쟁, 논쟁 또는 청구(각각 “**분쟁**”, 총칭하여 “**분쟁들**”)의 해결을 신속히 하고 그 비용을 통제하기 위해, 양 당사자는 법원 절차를 개시하기 전에 먼저 최소 삼십(30)일 동안 모든 분쟁(아래에 명시적으로 규정된 분쟁은 제외)을 비공식적으로 협상하려고 시도하는 데 동의합니다. 이러한 비공식 협상은 한 당사자가 다른 당사자에게 서면 통지를 한 때부터 시작됩니다.',
          { h3: '비공식 협상의 예외' },
          '양 당사자는 다음 분쟁에는 비공식 협상에 관한 위 규정이 적용되지 않는다는 데 동의합니다: (a) 당사자의 지식재산권을 집행 또는 보호하려 하거나 그 유효성에 관한 분쟁; (b) 절도, 불법 복제, 사생활 침해 또는 무단 이용의 주장과 관련되거나 그로부터 발생하는 분쟁; 그리고 (c) 금지명령에 의한 구제 청구.'
        ]
      },
      {
        id: 'corrections',
        heading: '15. 정정',
        blocks: [
          '서비스에는 설명, 가격, 이용 가능 여부 및 기타 다양한 정보를 포함하여 오탈자, 부정확한 내용 또는 누락이 있는 정보가 포함될 수 있습니다. 당사는 사전 통지 없이 언제든지 오류, 부정확한 내용 또는 누락을 정정하고 서비스의 정보를 변경하거나 업데이트할 권리를 보유합니다.'
        ]
      },
      {
        id: 'disclaimer',
        heading: '16. 면책 조항',
        blocks: [
          '서비스는 “있는 그대로” 및 “이용 가능한 상태로” 제공됩니다. 귀하는 서비스 이용에 따른 위험을 전적으로 귀하가 부담한다는 데 동의합니다. 법률이 허용하는 최대 범위 내에서, 당사는 서비스 및 귀하의 서비스 이용과 관련하여 상품성, 특정 목적에의 적합성 및 비침해에 대한 묵시적 보증을 포함하되 이에 국한되지 않는 명시적 또는 묵시적인 모든 보증을 부인합니다. 당사는 서비스 콘텐츠 또는 서비스에 링크된 웹사이트나 모바일 애플리케이션 콘텐츠의 정확성이나 완전성에 대해 어떠한 보증이나 진술도 하지 않으며, 다음에 대해 어떠한 법적 책임이나 기타 책임도 지지 않습니다: (1) 콘텐츠 및 자료의 오류, 실수 또는 부정확성, (2) 귀하의 서비스 접근 및 이용으로 인해 발생하는 성질을 불문한 신체 상해 또는 재산 피해, (3) 당사의 보안 서버 및/또는 그에 저장된 모든 개인정보 및/또는 금융 정보에 대한 무단 접근 또는 이용, (4) 서비스와의 전송 중단 또는 중지, (5) 제3자가 서비스로 또는 서비스를 통해 전송할 수 있는 버그, 바이러스, 트로이 목마 또는 이와 유사한 것, 및/또는 (6) 콘텐츠 및 자료의 오류나 누락, 또는 서비스를 통해 게시, 전송 또는 기타 방식으로 제공된 콘텐츠의 이용으로 인해 발생한 모든 종류의 손실이나 손해.',
          '당사는 서비스, 하이퍼링크된 웹사이트, 또는 배너나 기타 광고에 소개된 웹사이트나 모바일 애플리케이션을 통해 제3자가 광고하거나 제공하는 제품 또는 서비스에 대해 보증, 지지, 보장하거나 책임을 지지 않으며, 귀하와 제3자 제품 또는 서비스 제공업체 간의 거래에 당사자가 되거나 그 거래를 모니터링할 책임을 어떠한 방식으로도 지지 않습니다.',
          '어떠한 매체나 환경에서든 제품 또는 서비스를 구매할 때와 마찬가지로, 귀하는 최선의 판단을 내리고 적절한 경우 주의를 기울여야 합니다.',
          '**위치 데이터 면책 조항:** 서비스는 여행 기록 기능을 제공하기 위해 위치 데이터에 의존합니다. 당사는 위치 데이터의 정확성, 완전성 또는 신뢰성을 보장하지 않습니다. 위치 추적은 모든 지역이나 조건에서 제대로 작동하지 않을 수 있습니다. 귀하는 위치 기반 기능에 내재적인 한계와 부정확성이 있을 수 있음을 인정합니다.'
        ]
      },
      {
        id: 'liability',
        heading: '17. 책임의 제한',
        blocks: [
          '어떠한 경우에도 당사 또는 당사의 이사, 직원 또는 대리인은 귀하의 서비스 이용으로 인해 발생하는 일실 이익, 수익 손실, 데이터 손실 또는 기타 손해를 포함한 직접 손해, 간접 손해, 결과적 손해, 본보기적 손해, 부수적 손해, 특별 손해 또는 징벌적 손해에 대해, 당사가 그러한 손해의 가능성을 사전에 통지받은 경우에도 귀하 또는 제3자에게 책임을 지지 않습니다.',
          '본 약관에 이와 상반되는 내용이 있더라도, 원인과 소송 형태를 불문하고 귀하에 대한 당사의 책임은 언제나 소송 원인이 발생하기 전 육(6)개월 동안 귀하가 당사에 지불한 금액(있는 경우)으로 제한됩니다.',
          '미국의 일부 주법 및 국제법은 묵시적 보증에 대한 제한이나 특정 손해의 배제 또는 제한을 허용하지 않습니다. 이러한 법률이 귀하에게 적용되는 경우 위의 면책 또는 제한의 일부 또는 전부가 귀하에게 적용되지 않을 수 있으며, 귀하는 추가적인 권리를 가질 수 있습니다.'
        ]
      },
      {
        id: 'indemnification',
        heading: '18. 손해배상',
        blocks: [
          '귀하는 (1) 귀하의 기여물; (2) 서비스 이용; (3) 본 약관 위반; (4) 본 약관에 명시된 귀하의 진술 및 보증 위반; (5) 지식재산권을 포함하되 이에 국한되지 않는 제3자의 권리에 대한 귀하의 침해; 또는 (6) 귀하가 서비스를 통해 연결된 서비스의 다른 사용자에 대한 명백한 유해 행위로 인해 또는 이로부터 발생하여 제3자가 제기하는 모든 손실, 손해, 책임, 청구 또는 요구(합리적인 변호사 보수 및 비용 포함)로부터, 당사의 자회사, 계열사 및 당사 각각의 모든 임원, 대리인, 파트너, 직원을 포함한 당사를 방어하고, 배상하며, 면책하는 데 동의합니다.',
          '앞서 말한 내용에도 불구하고, 당사는 귀하의 비용으로 귀하가 당사에 배상해야 하는 모든 사안에 대한 독점적 방어 및 통제권을 가질 권리를 보유하며, 귀하는 귀하의 비용으로 그러한 청구에 대한 당사의 방어에 협조하는 데 동의합니다. 당사는 본 배상의 대상이 되는 청구, 소송 또는 절차를 알게 되는 즉시 이를 귀하에게 통지하기 위해 합리적인 노력을 기울입니다.'
        ]
      },
      {
        id: 'user-data',
        heading: '19. 사용자 데이터',
        blocks: [
          '당사는 서비스의 성능 관리를 위해 귀하가 서비스에 전송하는 특정 데이터와 귀하의 서비스 이용에 관한 데이터를 보관합니다. 당사는 정기적으로 데이터를 백업하지만, 귀하가 전송하는 모든 데이터 또는 귀하가 서비스를 이용하여 수행한 활동과 관련된 모든 데이터에 대한 책임은 전적으로 귀하에게 있습니다.',
          '귀하는 그러한 데이터의 손실 또는 손상에 대해 당사가 귀하에게 어떠한 책임도 지지 않는다는 데 동의하며, 이로써 그러한 데이터의 손실 또는 손상으로 인해 발생하는 당사에 대한 모든 소송 권리를 포기합니다.',
          '귀하는 서비스를 통해 언제든지 여행 데이터를 내보낼 수 있습니다. 중요한 여행 추억과 사진을 정기적으로 백업하실 것을 권장합니다.'
        ]
      },
      {
        id: 'electronic',
        heading: '20. 전자 통신, 거래 및 서명',
        blocks: [
          '서비스 방문, 당사에 대한 이메일 발송 및 온라인 양식 작성은 전자 통신에 해당합니다. 귀하는 전자 통신 수신에 동의하며, 당사가 이메일 및 서비스를 통해 전자적으로 귀하에게 제공하는 모든 계약, 통지, 공개 및 기타 통신이 해당 통신을 서면으로 해야 한다는 모든 법적 요건을 충족한다는 데 동의합니다.',
          '귀하는 이로써 전자 서명, 계약, 주문 및 기타 기록의 사용과, 당사가 또는 서비스를 통해 개시하거나 완료한 거래의 통지, 정책 및 기록의 전자적 전달에 동의합니다. 귀하는 이로써 원본 서명이나 비전자적 기록의 전달 또는 보관을 요구하거나, 전자적 수단 이외의 방법에 의한 지급 또는 크레딧 부여를 요구하는 모든 관할권의 법령, 규정, 규칙, 조례 또는 기타 법률에 따른 모든 권리나 요건을 포기합니다.'
        ]
      },
      {
        id: 'misc',
        heading: '21. 기타',
        blocks: [
          '본 약관과 당사가 서비스에 또는 서비스와 관련하여 게시한 모든 정책이나 운영 규칙은 귀하와 당사 간의 완전한 합의와 이해를 구성합니다. 당사가 본 약관의 권리나 조항을 행사하거나 집행하지 않더라도 이는 해당 권리나 조항의 포기로 간주되지 않습니다.',
          '본 약관은 법률이 허용하는 최대 범위 내에서 효력을 가집니다. 당사는 언제든지 당사의 권리와 의무의 전부 또는 일부를 타인에게 양도할 수 있습니다. 당사는 당사의 합리적인 통제를 벗어난 원인으로 인한 손실, 손해, 지연 또는 불이행에 대해 책임을 지지 않습니다.',
          '본 약관의 조항 또는 조항의 일부가 불법이거나, 무효이거나, 집행 불가능하다고 판단되는 경우, 해당 조항 또는 조항의 일부는 본 약관에서 분리 가능한 것으로 간주되며 나머지 조항의 유효성과 집행 가능성에 영향을 미치지 않습니다.',
          '본 약관 또는 서비스 이용으로 인해 귀하와 당사 간에 합작 투자, 파트너십, 고용 또는 대리 관계가 형성되지 않습니다. 귀하는 당사가 본 약관을 작성했다는 이유로 본 약관이 당사에 불리하게 해석되지 않는다는 데 동의합니다. 귀하는 이로써 본 약관의 전자적 형식 및 본 약관을 체결하기 위한 당사자들의 서명 부재에 근거하여 귀하가 가질 수 있는 모든 항변을 포기합니다.'
        ]
      },
      {
        id: 'contact',
        heading: '22. 문의처',
        blocks: [
          '서비스에 관한 불만을 해결하거나 서비스 이용에 관한 추가 정보를 받으려면 다음으로 연락해 주십시오.',
          `**${LEGAL_NAME} (Travo Labs라는 이름으로 운영)** — 이메일: ${EMAIL}`
        ]
      }
    ]
  }
};
